import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Shield,
  Activity,
  BarChart3,
  Sparkles,
  Loader2,
  Send,
  Trash2,
  UserPlus,
  Crown,
  Users,
  CreditCard,
  HardDrive,
  Search,
  Pencil,
  X,
  AlertTriangle,
  Globe2,
  MapPin,
  RefreshCw,
  FileText,
  Image as ImageIcon,
  File,
  Database,
  CalendarClock,
  Clock,
  Award,
  Save,
  Check,
  Wand2,
  Plus,
  BookOpen,
  Eye,
  EyeOff,
  Settings2,
  Zap,
  Copy,
  Code,
  MessageCircle,
  KeyRound,
  Lock,
  Play,
  Mail,
  TrendingUp,
  Monitor,
  MailCheck,
  Target,
  ExternalLink,
  CheckCircle2,
  UserCheck,
  UserX,
  ClipboardList,
} from "lucide-react";
import { api } from "../api";
import { toHandle } from "../utils/handle";
import IntegrationsModule from "./admin/IntegrationsModule";
const PLAN_COLORS = {
  free: "#94a3b8",
  pro: "#a78bfa",
  business: "#3b82f6",
};
const fmtBytes = (n) => {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const adminApi = {
  stats: () => api.get("/admin/stats").then((r) => r.data),
  users: () => api.get("/admin/users").then((r) => r.data),
  userAccess: () => api.get("/admin/user-access").then((r) => r.data),
  patch: (id, body) => api.patch(`/admin/users/${id}`, body).then((r) => r.data),
  remove: (id) => api.delete(`/admin/users/${id}`).then((r) => r.data),
  create: (body) => api.post("/admin/users", body).then((r) => r.data),
  aiParse: (command) =>
    api.post("/admin/ai/command", { command }).then((r) => r.data),
  aiExecute: (intent, parameters) =>
    api.post("/admin/ai/execute", { intent, parameters }).then((r) => r.data),
  health: () => api.get("/admin/health").then((r) => r.data),
  cyber: () => api.get("/admin/cybersecurity").then((r) => r.data),
  analytics: () => api.get("/admin/analytics").then((r) => r.data),
  seogeo: () => api.get("/admin/seo-geo").then((r) => r.data),
};

export default function AdminAI() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("overview"); // "overview" | "access-storage"

  // Access & Storage module
  const [accessData, setAccessData] = useState(null);
  const [accessBusy, setAccessBusy] = useState(false);
  const [accessSort, setAccessSort] = useState("storage_desc"); // storage_desc | signup_desc | idle_desc | name_asc
  const [accessQ, setAccessQ] = useState("");

  // AI command bar
  const [cmd, setCmd] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResp, setAiResp] = useState(null);
  const [confirm, setConfirm] = useState(null); // pending destructive action

  // Editing modals
  const [editUser, setEditUser] = useState(null);
  const [appsUser, setAppsUser] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  // Sub-modules
  const [health, setHealth] = useState(null);
  const [cyber, setCyber] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [seogeo, setSeogeo] = useState(null);

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const [s, u, h, c, a, sg] = await Promise.all([
        adminApi.stats(),
        adminApi.users(),
        adminApi.health().catch(() => null),
        adminApi.cyber().catch(() => null),
        adminApi.analytics().catch(() => null),
        adminApi.seogeo().catch(() => null),
      ]);
      setStats(s); setUsers(u); setHealth(h); setCyber(c); setAnalytics(a); setSeogeo(sg);
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const loadAccessData = useCallback(async () => {
    setAccessBusy(true);
    try {
      const d = await adminApi.userAccess();
      setAccessData(d);
    } catch (e) {
      console.error("Failed to load user-access:", e);
    } finally {
      setAccessBusy(false);
    }
  }, []);

  useEffect(() => {
    if (view === "access-storage" && !accessData) loadAccessData();
  }, [view, accessData, loadAccessData]);

  // Referrals module
  const [referralsData, setReferralsData] = useState(null);
  const [referralsBusy, setReferralsBusy] = useState(false);
  const loadReferralsData = useCallback(async () => {
    setReferralsBusy(true);
    try {
      const { data } = await api.get("/admin/referrals");
      setReferralsData(data);
    } catch (e) {
      console.error("Failed to load referrals:", e);
    } finally {
      setReferralsBusy(false);
    }
  }, []);
  useEffect(() => {
    if (view === "referrals" && !referralsData) loadReferralsData();
  }, [view, referralsData, loadReferralsData]);

  // SEO+GEO module
  const [seoGeoData, setSeoGeoData] = useState(null);
  const [seoGeoBusy, setSeoGeoBusy] = useState(false);
  const loadSeoGeoData = useCallback(async () => {
    setSeoGeoBusy(true);
    try {
      const { data } = await api.get("/admin/seo-geo");
      setSeoGeoData(data);
    } catch (e) {
      console.error("Failed to load seo-geo:", e);
    } finally {
      setSeoGeoBusy(false);
    }
  }, []);
  useEffect(() => {
    if (view === "seogeo" && !seoGeoData) loadSeoGeoData();
  }, [view, seoGeoData, loadSeoGeoData]);

  const filtered = useMemo(() => {
    let rows = users;
    if (filter === "active") rows = rows.filter((u) => u.is_active);
    if (filter === "inactive") rows = rows.filter((u) => !u.is_active);
    if (filter === "individual") rows = rows.filter((u) => u.account_type === "individual");
    if (filter === "business") rows = rows.filter((u) => u.account_type === "business");
    if (["free", "pro", "business"].includes(filter) && filter !== "business") rows = rows.filter((u) => u.plan === filter);
    const s = q.trim().toLowerCase();
    if (s) rows = rows.filter((u) =>
      (u.address || "").toLowerCase().includes(s) ||
      (u.name || "").toLowerCase().includes(s)
    );
    return rows;
  }, [users, filter, q]);

  const runAI = async (e) => {
    e?.preventDefault?.();
    const text = cmd.trim();
    if (!text) return;
    setAiBusy(true);
    setAiResp(null);
    try {
      const parsed = await adminApi.aiParse(text);
      setAiResp(parsed);
      if (parsed.intent === "unknown") return;
      if (parsed.requires_confirmation || parsed.intent === "delete_user") {
        setConfirm(parsed);
      } else {
        const r = await adminApi.aiExecute(parsed.intent, parsed.parameters || {});
        setAiResp({ ...parsed, executed: true, result: r });
        await refresh();
      }
    } catch (err) {
      setAiResp({ intent: "error", message: err?.response?.data?.detail || err.message });
    } finally {
      setAiBusy(false);
      setCmd("");
    }
  };

  const confirmExecute = async () => {
    if (!confirm) return;
    setAiBusy(true);
    try {
      const r = await adminApi.aiExecute(confirm.intent, confirm.parameters || {});
      setAiResp({ ...confirm, executed: true, result: r });
      setConfirm(null);
      await refresh();
    } catch (err) {
      setAiResp({ intent: "error", message: err?.response?.data?.detail || err.message });
      setConfirm(null);
    } finally {
      setAiBusy(false);
    }
  };

  const saveEdit = async (patch) => {
    if (!editUser) return;
    await adminApi.patch(editUser.id, patch);
    setEditUser(null);
    await refresh();
  };

  const deleteUser = async (u) => {
    await adminApi.remove(u.id);
    await refresh();
  };

  const createUser = async (form) => {
    await adminApi.create(form);
    setCreateOpen(false);
    await refresh();
  };

  return (
    <div className="h-full overflow-y-auto bg-white text-slate-800" data-testid="app-admin-ai">
      <div className="p-4 max-w-[1280px] mx-auto space-y-5">
        {/* Header — colorful gradient badge with white font */}
        <div
          className="flex items-center gap-3 rounded-2xl p-4 hexa-keep-white"
          style={{
            background: "linear-gradient(120deg, #fbbf24, #f43f5e, #a855f7, #22d3ee)",
            boxShadow: "0 12px 28px rgba(167,139,250,0.25), 0 4px 12px rgba(15,23,42,0.06)",
          }}
        >
          <div
            className="grid place-items-center w-11 h-11 rounded-xl"
            style={{
              background: "rgba(255,255,255,0.22)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,0.4)",
            }}
          >
            <Crown size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-wide text-white">HEXA Admin AI</h1>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-white/90">// super-admin console · everything, orchestrated</div>
          </div>
        </div>

        {/* View tabs — colorful pill buttons on white */}
        <div className="flex flex-wrap items-center gap-2">
          {[
            { k: "overview",       label: "Overview",             Icon: BarChart3,      tint: "#f59e0b" },
            { k: "access-storage", label: "Access & Storage",     Icon: Database,       tint: "#8b5cf6" },
            { k: "integrations",   label: "Integrations",         Icon: Zap,            tint: "#38bdf8" },
            { k: "referrals",      label: "Referrals",            Icon: Award,          tint: "#ec4899" },
            { k: "seogeo",         label: "AI SEO & GEO",         Icon: Globe2,         tint: "#22c55e" },
            { k: "analytics",      label: "Website Analytics",    Icon: Monitor,        tint: "#06b6d4" },
            { k: "cos",            label: "COS Emails",           Icon: MailCheck,      tint: "#f97316" },
            { k: "gads",           label: "Google Ads",           Icon: Target,         tint: "#4285F4" },
            { k: "approvals",      label: "Pending Approvals",    Icon: ClipboardList,  tint: "#ef4444" },
          ].map(({ k, label, Icon, tint }) => {
            const active = view === k;
            return (
              <button
                key={k}
                data-testid={`admin-view-${k}`}
                onClick={() => setView(k)}
                className="px-4 py-2 rounded-xl mono text-[10px] uppercase tracking-[0.24em] flex items-center gap-2 transition-all font-medium"
                style={{
                  background: active
                    ? `linear-gradient(120deg, ${tint}, ${tint}bb)`
                    : "#ffffff",
                  color: active ? "#ffffff" : "#334155",
                  border: `1px solid ${active ? tint : "#e2e8f0"}`,
                  boxShadow: active
                    ? `0 8px 20px ${tint}55`
                    : "0 1px 2px rgba(15,23,42,0.05)",
                }}
              >
                <Icon size={12} color={active ? "#ffffff" : tint} /> {label}
              </button>
            );
          })}
        </div>

        {view === "access-storage" && (
          <AccessStorageModule
            data={accessData}
            busy={accessBusy}
            onRefresh={loadAccessData}
            sort={accessSort}
            setSort={setAccessSort}
            q={accessQ}
            setQ={setAccessQ}
          />
        )}

        {view === "referrals" && (
          <ReferralsModule
            data={referralsData}
            busy={referralsBusy}
            onRefresh={loadReferralsData}
          />
        )}

        {view === "integrations" && (
          <IntegrationsModule />
        )}

        {view === "seogeo" && (
          <SeoGeoWorkspace
            data={seoGeoData}
            busy={seoGeoBusy}
            onRefresh={loadSeoGeoData}
          />
        )}

        {view === "analytics" && <WebsiteAnalyticsPanel />}

        {view === "cos" && <CosEmailPanel />}

        {view === "gads" && <GoogleAdsPanel />}

        {view === "approvals" && <PendingApprovalsPanel />}

        {view === "overview" && <>

        {/* Stat tiles */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Tile icon={Users} label="Total users" value={stats.total_users} tint="#a78bfa" />
            <Tile icon={Users} label="Individuals" value={stats.individuals} tint="#60a5fa" />
            <Tile icon={Users} label="Business" value={stats.businesses} tint="#34d399" />
            <Tile icon={Activity} label="Active (7d)" value={stats.active_7d} tint="#22d3ee" sub={`${stats.inactive_7d} inactive`} />
            <Tile icon={CreditCard} label="MRR (incl. 18% GST)" value={fmtINR(stats.monthly_revenue_inr)} tint="#fbbf24" sub={`storage ${fmtBytes(stats.total_storage_bytes)}`} />
          </div>
        )}

        {/* AI command bar */}
        <form onSubmit={runAI} data-testid="admin-ai-form">
          <div className="flex items-center gap-2 rounded-xl px-3 py-2"
            style={{ background: "#ffffff", border: "1px solid rgba(167,139,250,0.35)", boxShadow: "0 0 24px rgba(124,58,237,0.25)" }}>
            <Sparkles size={16} className="text-[#6d28d9]" />
            <input
              data-testid="admin-ai-input"
              value={cmd}
              onChange={(e) => setCmd(e.target.value)}
              placeholder='Ask HEXA Admin AI… e.g. "set rahul to pro plan", "delete demo user", "show inactive users"'
              className="flex-1 bg-transparent outline-none text-sm text-white/90 placeholder:text-white/40"
            />
            <button
              type="submit"
              data-testid="admin-ai-submit"
              disabled={aiBusy || !cmd.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs"
              style={{ border: "1px solid rgba(139,92,246,0.45)", color: "#c7c1ff" }}>
              {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Run
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {["show stats", "list all users", "show inactive users", "set abc to pro plan", "list users on free plan"].map(s => (
              <button key={s} type="button" onClick={() => setCmd(s)}
                className="text-[10px] mono uppercase tracking-widest text-white/55 hover:text-white px-2.5 py-1 rounded-full"
                style={{ background: "rgba(15,23,42,0.05)" }}>
                {s}
              </button>
            ))}
          </div>
          {aiResp && (
            <div className="mt-2 px-3 py-2 rounded-md text-xs flex items-start gap-2"
              style={{ background: "rgba(15,23,42,0.04)", border: "1px solid rgba(15,23,42,0.10)" }}>
              <Sparkles size={12} className="text-[#6d28d9] mt-0.5" />
              <div>
                <div className="text-white/90">{aiResp.message}</div>
                {aiResp.executed && <div className="text-emerald-300/80 text-[10px] mono uppercase tracking-wider mt-1">// executed</div>}
              </div>
            </div>
          )}
        </form>

        {/* User table */}
        <div className="rounded-xl" style={{ background: "#ffffff", border: "1px solid rgba(15,23,42,0.10)" }}>
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-white/8">
            <div className="text-sm font-medium">Users</div>
            <div className="ml-auto flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs"
                style={{ background: "rgba(15,23,42,0.05)", border: "1px solid rgba(15,23,42,0.08)" }}>
                <Search size={12} className="text-white/50" />
                <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="filter…" className="bg-transparent outline-none text-xs w-32"/>
              </div>
              <select data-testid="admin-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
                className="bg-[#0c1224] text-xs rounded px-2 py-1" style={{ border: "1px solid rgba(15,23,42,0.10)" }}>
                <option value="all">all</option>
                <option value="active">active</option>
                <option value="inactive">inactive</option>
                <option value="individual">individual</option>
                <option value="business">business acct</option>
                <option value="free">free plan</option>
                <option value="pro">pro plan</option>
              </select>
              <button onClick={() => setCreateOpen(true)} data-testid="admin-add-user"
                className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs"
                style={{ border: "1px solid rgba(139,92,246,0.45)", color: "#c7c1ff" }}>
                <UserPlus size={12} /> Add user
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-white/45 mono uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">User</th>
                  <th className="text-left px-3 py-2">Contact</th>
                  <th className="text-left px-3 py-2">Users</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Plan</th>
                  <th className="text-left px-3 py-2">Storage</th>
                  <th className="text-left px-3 py-2">Active</th>
                  <th className="text-left px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">Services</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const teamSize = u.team_size || u.employee_count || (u.account_type === "business" ? "—" : "1");
                  const startDate = (u.subscription_started || u.created || "").slice(0, 10) || "—";
                  const expiryDate = (u.subscription_expires || u.plan_expires_at || "").slice(0, 10) || "—";
                  const amount = u.amount_paid ?? (u.plan === "business" ? 250 : u.plan === "pro" ? 150 : 0);
                  const enabled = u.enabled_apps || [];
                  const svcMap = [
                    { k: "projects", label: "PM",     tint: "#22c55e" },
                    { k: "crm",      label: "CRM",    tint: "#ec4899" },
                    { k: "novo",     label: "AI",     tint: "#a855f7" },
                    { k: "excel",    label: "Sheets", tint: "#0ea5e9" },
                    { k: "_apps",    label: "Apps",   tint: "#f59e0b" },
                  ];
                  const hasSvc = (k) => {
                    if (u.is_super_admin) return true;
                    if (!enabled.length) return true;                // no restrictions = all on
                    if (k === "_apps") return enabled.length > 5;    // "app access" flag = user has 5+ enabled apps
                    return enabled.includes(k);
                  };
                  return (
                    <tr key={u.id} data-testid={`admin-user-${u.address}`} className="border-t border-white/5 hover:bg-white/3 align-top">
                      <td className="px-3 py-2">
                        <div className="text-white/90 font-medium">{u.name} {u.is_super_admin && <span className="text-[9px] mono text-[#b45309] ml-1">SUPER</span>}</div>
                        <div className="text-white/45 text-[10px] mono">{u.company_name || u.workspace_name || "—"}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-white/85 mono text-[10.5px]">{u.mobile || u.phone || "—"}</div>
                        <div className="text-white/45 text-[10px] mono">{u.handle || toHandle(u.address)}</div>
                      </td>
                      <td className="px-3 py-2 text-white/70 mono text-[11px] text-center">{teamSize}</td>
                      <td className="px-3 py-2">
                        <span className="mono text-[10px] uppercase tracking-wide text-white/60">{u.account_type}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className="px-2 py-0.5 rounded-full mono text-[10px] uppercase tracking-wider"
                          style={{ background: `${PLAN_COLORS[u.plan]}22`, color: PLAN_COLORS[u.plan], border: `1px solid ${PLAN_COLORS[u.plan]}55` }}>{u.plan}</span>
                      </td>
                      <td className="px-3 py-2 min-w-[140px]">
                        <div className="mono text-[10.5px] text-white/70">{fmtBytes(u.storage_used_bytes)} / {fmtBytes(u.storage_quota_bytes)}</div>
                        <div className="h-1 mt-1 rounded-full bg-white/8 overflow-hidden">
                          <div className="h-full" style={{ width: `${Math.min(100, u.storage_pct)}%`, background: u.storage_pct > 80 ? "#f87171" : "#a78bfa" }} />
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className={`mono text-[10px] uppercase tracking-wider mb-0.5 ${u.is_active ? "text-emerald-300" : "text-white/40"}`}>
                          {u.is_active ? "● online" : "○ offline"}
                        </div>
                        <div className="text-[9.5px] text-white/45 mono leading-tight">Start {startDate}</div>
                        <div className="text-[9.5px] text-white/45 mono leading-tight">Exp&nbsp;&nbsp; {expiryDate}</div>
                      </td>
                      <td className="px-3 py-2 mono text-[11px] text-white/80 whitespace-nowrap">₹{Number(amount || 0).toLocaleString("en-IN")}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {svcMap.map(s => {
                            const on = hasSvc(s.k);
                            return (
                              <span key={s.k} className="mono text-[9px] px-1.5 py-0.5 rounded uppercase tracking-widest" style={{
                                color: on ? s.tint : "#475569",
                                background: on ? `${s.tint}18` : "rgba(148,163,184,0.10)",
                                border: `1px solid ${on ? `${s.tint}66` : "rgba(148,163,184,0.30)"}`,
                              }}>{s.label}</span>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={() => setAppsUser(u)} data-testid={`admin-apps-${u.address}`}
                          title="App access"
                          className="p-1 rounded hover:bg-white/10 text-sky-400 mr-1"><Zap size={11} /></button>
                        <button
                          onClick={async () => {
                            try {
                              const { data } = await api.post(`/admin/cos/send-test/${encodeURIComponent(u.address)}`);
                              alert(data.message || "Reports sent.");
                            } catch (e) {
                              alert(e?.response?.data?.detail || "Could not send the test report.");
                            }
                          }}
                          data-testid={`admin-test-report-${u.address}`}
                          title="Send test report (daily reports for this user's apps)"
                          className="p-1 rounded hover:bg-white/10 text-emerald-400 mr-1"><Send size={11} /></button>
                        <button onClick={() => setEditUser(u)} data-testid={`admin-edit-${u.address}`}
                          className="p-1 rounded hover:bg-white/10 text-white/70 mr-1"><Pencil size={11} /></button>
                        {!u.is_super_admin && (
                          <button onClick={() => { if (window.confirm(`Delete ${u.address}?`)) deleteUser(u); }}
                            data-testid={`admin-delete-${u.address}`}
                            className="p-1 rounded hover:bg-white/10 text-red-400"><Trash2 size={11} /></button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={10} className="text-center py-6 text-white/40 text-xs">No users match this filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* AI sub-modules */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <ModuleCard title="AI Cybersecurity" icon={Shield} tint="#34d399" data={cyber}>
            {cyber && (
              <>
                <div className="text-2xl font-light text-emerald-300">{cyber.score}<span className="text-xs text-white/40">/100</span></div>
                <ul className="mt-2 space-y-1 text-[11px]">
                  {(cyber.alerts || []).map((a, i) => <li key={i} className="text-white/70">· {a.msg}</li>)}
                </ul>
              </>
            )}
          </ModuleCard>
          <ModuleCard title="OS Health Assistant" icon={Activity} tint="#22d3ee" data={health}>
            {health && (
              <>
                <div className="text-2xl font-light text-cyan-300">{health.db_latency_ms?.toFixed?.(1) ?? health.db_latency_ms}<span className="text-xs text-white/40"> ms</span></div>
                <div className="text-[11px] text-white/55 mt-1">DB: {health.db_ok ? "OK" : "DOWN"} · AI key: {health.ai_key_configured ? "ON" : "OFF"}</div>
                <div className="text-[11px] text-white/55 mt-1.5 mono">users {health.counts?.users} · files {health.counts?.files} · chats {health.counts?.chats}</div>
              </>
            )}
          </ModuleCard>
          <ModuleCard title="AI Analytics" icon={BarChart3} tint="#a78bfa" data={analytics}>
            {analytics && (
              <>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-light text-violet-300">{(analytics.by_plan?.pro || 0) + (analytics.by_plan?.business || 0)}</span>
                  <span className="text-xs text-white/40">paid users</span>
                </div>
                <ul className="mt-2 space-y-0.5 text-[11px]">
                  {(analytics.top_apps || []).slice(0, 3).map((a) => (
                    <li key={a.app} className="flex justify-between text-white/70"><span>{a.app}</span><span className="mono">{a.score}</span></li>
                  ))}
                </ul>
              </>
            )}
          </ModuleCard>
          <ModuleCard title="AI SEO + GEO" icon={Globe2} tint="#fbbf24" data={seogeo}>
            {seogeo && (
              <>
                <div className="text-[11px] text-white/70 flex items-center gap-1.5"><Globe2 size={11}/> SEO: {seogeo.seo?.status}</div>
                <div className="text-[11px] text-white/70 flex items-center gap-1.5 mt-1"><MapPin size={11}/> GEO: {seogeo.geo?.status}</div>
                <div className="text-[10px] text-white/40 mt-2 leading-relaxed">{seogeo.seo?.notes}</div>
              </>
            )}
          </ModuleCard>
        </div>

        {/* Pricing reference card */}
        {stats?.pricing && (
          <div className="rounded-xl p-4" style={{ background: "#ffffff", border: "1px solid rgba(15,23,42,0.08)" }}>
            <div className="text-sm font-medium mb-2 flex items-center gap-2"><HardDrive size={14}/> Pricing & quotas <span className="mono text-[10px] text-white/40 uppercase tracking-widest">+ 18% GST</span></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {Object.entries(stats.pricing).map(([plan, p]) => (
                <div key={plan} className="rounded-lg p-3" style={{ background: "rgba(15,23,42,0.04)", border: `1px solid ${PLAN_COLORS[plan]}44` }}>
                  <div className="flex items-baseline justify-between">
                    <div className="text-sm uppercase tracking-wider font-medium" style={{ color: PLAN_COLORS[plan] }}>{plan}</div>
                    <div className="text-xs mono text-white/80">{p.monthly_inr ? `${fmtINR(p.monthly_inr)}/mo` : "Free"}</div>
                  </div>
                  <div className="text-[11px] text-white/55 mt-1">{p.storage_gb} GB storage</div>
                  <ul className="mt-2 space-y-0.5 text-[11px] text-white/70">
                    {(p.features || []).map((f) => <li key={f}>· {f}</li>)}
                  </ul>
                  {p.monthly_inr > 0 && (
                    <div className="mt-2 text-[10px] mono text-white/40">≈ {fmtINR((p.monthly_inr * 1.18).toFixed(2))} after GST</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {busy && (
          <div className="fixed bottom-3 right-3 text-[11px] mono text-white/60 px-2 py-1 rounded bg-black/60 border border-white/10 flex items-center gap-1.5">
            <Loader2 size={11} className="animate-spin"/> refreshing…
          </div>
        )}
        </>}
      </div>

      {/* Confirm destructive */}
      {confirm && (
        <Modal onClose={() => setConfirm(null)}>
          <div className="flex items-start gap-2 mb-3">
            <AlertTriangle className="text-amber-300" size={18}/>
            <div>
              <div className="text-sm font-medium">Confirm action</div>
              <div className="text-xs text-white/70 mt-0.5">{confirm.message}</div>
              <div className="text-[10px] mono uppercase tracking-wider text-white/50 mt-2">
                intent: {confirm.intent} · params: {JSON.stringify(confirm.parameters || {})}
              </div>
            </div>
          </div>
          <div className="flex gap-2 justify-end mt-3">
            <button onClick={() => setConfirm(null)} className="btn-ghost text-xs px-3 py-1.5">Cancel</button>
            <button data-testid="admin-confirm-yes" onClick={confirmExecute}
              className="btn-ghost text-xs px-3 py-1.5 text-red-300" style={{ border: "1px solid rgba(248,113,113,0.55)" }}>
              {aiBusy ? <Loader2 size={11} className="animate-spin"/> : "Yes, do it"}
            </button>
          </div>
        </Modal>
      )}

      {/* Edit user */}
      {editUser && <EditUserModal user={editUser} onClose={() => setEditUser(null)} onSave={saveEdit} />}
      {appsUser && <AppAccessModal user={appsUser} onClose={() => setAppsUser(null)} onSaved={() => { setAppsUser(null); loadUsers(); }} />}
      {createOpen && <CreateUserModal onClose={() => setCreateOpen(false)} onSave={createUser} />}
    </div>
  );
}

function Tile({ icon: Icon, label, value, tint, sub }) {
  return (
    <div className="rounded-xl p-3 flex items-center gap-3"
      style={{ background: "#ffffff", border: `1px solid ${tint}33` }}>
      <div className="grid place-items-center w-9 h-9 rounded-lg" style={{ background: `${tint}22` }}>
        <Icon size={16} color={tint} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] mono uppercase tracking-wider text-white/40">{label}</div>
        <div className="text-base text-white/90 leading-tight">{value}</div>
        {sub && <div className="text-[10px] text-white/40 mono">{sub}</div>}
      </div>
    </div>
  );
}

function ModuleCard({ title, icon: Icon, tint, children, data }) {
  return (
    <div className="rounded-xl p-3" style={{ background: "#ffffff", border: `1px solid ${tint}33` }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} color={tint} />
        <div className="text-sm font-medium text-white/85">{title}</div>
      </div>
      {data ? children : <div className="text-[11px] text-white/40">loading…</div>}
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-[300] grid place-items-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="rounded-xl p-4 w-full max-w-md" onClick={(e) => e.stopPropagation()}
        style={{ background: "#ffffff", color: "#0f172a", border: "1px solid #e2e8f0", boxShadow: "0 20px 40px rgba(15,23,42,0.18)" }}>
        <div className="flex justify-end mb-1"><button onClick={onClose}><X size={14} className="text-slate-500"/></button></div>
        {children}
      </div>
    </div>
  );
}

function EditUserModal({ user, onClose, onSave }) {
  const [form, setForm] = useState({
    name: user.name || "",
    plan: user.plan || "free",
    role: user.role || "employee",
    account_type: user.account_type || "individual",
    phone: user.phone || user.mobile || "",
    contact_email: user.contact_email || "",
    username: user.username || (user.address || "").split("@")[0],
    subscription_expires: user.subscription_expires ? user.subscription_expires.slice(0, 10) : "",
  });
  const extendBy = (days) => {
    const base = form.subscription_expires ? new Date(form.subscription_expires) : new Date();
    base.setDate(base.getDate() + days);
    setForm({ ...form, subscription_expires: base.toISOString().slice(0, 10) });
  };
  return (
    <Modal onClose={onClose}>
      <div className="text-sm font-medium mb-1">Edit {toHandle(user.address)}</div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <label>Name<input className="input-dark" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}/></label>
        <label>Phone<input data-testid="admin-edit-phone" className="input-dark" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}/></label>
        <label className="col-span-2">Username shown as @handle (display only, sign-in address does not change)
          <input data-testid="admin-edit-username" className="input-dark" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}/>
        </label>
        <label className="col-span-2">Contact email (daily reports and blog emails are delivered here)
          <input data-testid="admin-edit-contact-email" type="email" className="input-dark" placeholder="you@gmail.com" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}/>
        </label>
        <label>Plan
          <select className="input-dark" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
            <option value="free">Free</option><option value="pro">Pro</option><option value="business">Business</option>
          </select>
        </label>
        <label>Role
          <select className="input-dark" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="individual">individual</option><option value="employee">employee</option><option value="boss">boss</option>
          </select>
        </label>
        <label>Account type
          <select className="input-dark" value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })}>
            <option value="individual">individual</option><option value="business">business</option>
          </select>
        </label>
        <label>Access expires
          <input data-testid="admin-edit-expiry" type="date" className="input-dark" value={form.subscription_expires} onChange={(e) => setForm({ ...form, subscription_expires: e.target.value })}/>
        </label>
      </div>
      <div className="flex items-center gap-1.5 mt-2">
        <span className="text-[10px] text-white/40 mono">extend:</span>
        <button data-testid="admin-edit-extend-15" onClick={() => extendBy(15)} className="text-[10px] px-2 py-1 rounded border border-white/15 text-white/70 hover:bg-white/10">+15 days</button>
        <button data-testid="admin-edit-extend-30" onClick={() => extendBy(30)} className="text-[10px] px-2 py-1 rounded border border-white/15 text-white/70 hover:bg-white/10">+30 days</button>
        <button data-testid="admin-edit-extend-365" onClick={() => extendBy(365)} className="text-[10px] px-2 py-1 rounded border border-white/15 text-white/70 hover:bg-white/10">+1 year</button>
      </div>
      <div className="flex justify-end gap-2 mt-3">
        <button onClick={onClose} className="text-xs px-3 py-1.5 rounded border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 transition-colors">Cancel</button>
        <button data-testid="admin-edit-save" onClick={() => onSave({ ...form, subscription_expires: form.subscription_expires ? new Date(form.subscription_expires).toISOString() : undefined })} className="text-xs px-4 py-1.5 rounded bg-purple-600 text-white font-semibold hover:bg-purple-700 transition-colors">Save</button>
      </div>
    </Modal>
  );
}

function CreateUserModal({ onClose, onSave }) {
  const [form, setForm] = useState({ username: "", name: "", password: "demo1234", account_type: "individual", plan: "free" });
  return (
    <Modal onClose={onClose}>
      <div className="text-sm font-medium mb-1">Create new user</div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <label className="col-span-2">Username (will become user@hexawebos.com)
          <input className="input-dark" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}/>
        </label>
        <label>Display name<input className="input-dark" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}/></label>
        <label>Initial password<input className="input-dark" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}/></label>
        <label>Account type
          <select className="input-dark" value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })}>
            <option value="individual">individual</option><option value="business">business</option>
          </select>
        </label>
        <label>Plan
          <select className="input-dark" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
            <option value="free">Free</option><option value="pro">Pro</option><option value="business">Business</option>
          </select>
        </label>
      </div>
      <div className="flex justify-end gap-2 mt-3">
        <button onClick={onClose} className="text-xs px-3 py-1.5 rounded border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 transition-colors">Cancel</button>
        <button data-testid="admin-create-save" onClick={() => onSave(form)} disabled={!form.username.trim()}
          className="text-xs px-4 py-1.5 rounded bg-purple-600 text-white font-semibold hover:bg-purple-700 disabled:opacity-50 transition-colors">Create</button>
      </div>
    </Modal>
  );
}


// ─────────────────────────────────────────────────────────────────────
// User Access & Storage Module
//   → who signed up, when, last active, plan, and Files-App storage usage
// ─────────────────────────────────────────────────────────────────────
const FILE_TYPE_COLORS = {
  image: "#ec4899",
  photo: "#ec4899",
  doc: "#3b82f6",
  document: "#3b82f6",
  pdf: "#f43f5e",
  sheet: "#22c55e",
  spreadsheet: "#22c55e",
  slide: "#f59e0b",
  presentation: "#f59e0b",
  audio: "#a78bfa",
  video: "#06b6d4",
  other: "#94a3b8",
};

function fmtDaysAgo(days) {
  if (days == null) return "—";
  if (days === 0) return "Today";
  if (days === 1) return "1 day ago";
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function AccessStorageModule({ data, busy, onRefresh, sort, setSort, q, setQ }) {
  const sortedFiltered = useMemo(() => {
    if (!data) return [];
    let rows = [...data.users];
    const query = (q || "").trim().toLowerCase();
    if (query) {
      rows = rows.filter((u) =>
        (u.address || "").toLowerCase().includes(query) ||
        (u.name || "").toLowerCase().includes(query) ||
        (u.company_name || "").toLowerCase().includes(query)
      );
    }
    switch (sort) {
      case "storage_desc": rows.sort((a, b) => b.storage_used_bytes - a.storage_used_bytes); break;
      case "signup_desc":  rows.sort((a, b) => (b.created || "").localeCompare(a.created || "")); break;
      case "idle_desc":    rows.sort((a, b) => (b.idle_days ?? 9999) - (a.idle_days ?? 9999)); break;
      case "name_asc":     rows.sort((a, b) => (a.name || "").localeCompare(b.name || "")); break;
      default: break;
    }
    return rows;
  }, [data, sort, q]);

  if (busy && !data) {
    return (
      <div className="rounded-xl p-8 text-center text-white/55 text-sm"
        style={{ background: "#ffffff", border: "1px solid rgba(15,23,42,0.10)" }}>
        <Loader2 size={16} className="animate-spin mx-auto mb-2 text-[#6d28d9]" />
        Loading user access & storage report…
      </div>
    );
  }
  if (!data) return null;

  const { totals, top_storage } = data;
  const totalStorageGB = (totals.total_storage_bytes / (1024 ** 3)).toFixed(2);

  return (
    <div className="space-y-4" data-testid="admin-access-storage">
      {/* Global app on/off — quickly disable an entire app for the whole workspace */}
      <GlobalAppsCard />

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Database size={14} className="text-[#6d28d9]" />
        <div className="text-sm font-medium">User Access & Cloud Storage</div>
        <span className="mono text-[10px] uppercase tracking-[0.28em] text-white/40">// files app usage per user</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs"
            style={{ background: "rgba(15,23,42,0.05)", border: "1px solid rgba(15,23,42,0.08)" }}>
            <Search size={12} className="text-white/50" />
            <input
              data-testid="admin-access-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="filter by name, email, company…"
              className="bg-transparent outline-none text-xs w-56"
            />
          </div>
          <select
            data-testid="admin-access-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-[#0c1224] text-xs rounded px-2 py-1"
            style={{ border: "1px solid rgba(15,23,42,0.10)" }}
          >
            <option value="storage_desc">Storage: high → low</option>
            <option value="signup_desc">Newest signups</option>
            <option value="idle_desc">Most idle first</option>
            <option value="name_asc">Name A → Z</option>
          </select>
          <button
            data-testid="admin-access-refresh"
            onClick={onRefresh}
            disabled={busy}
            className="btn-ghost text-xs flex items-center gap-1 px-2 py-1"
            title="Refresh"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          </button>
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {[
          ["Users", totals.total_users, "#a78bfa", Users],
          ["Active (7d)", totals.active_7d, "#22c55e", Activity],
          ["Signups 7d", totals.signups_last_7d, "#60a5fa", UserPlus],
          ["Signups 30d", totals.signups_last_30d, "#3b82f6", CalendarClock],
          ["Total files", totals.total_files, "#f59e0b", FileText],
          ["Storage used", `${totalStorageGB} GB`, "#ec4899", HardDrive],
        ].map(([label, value, color, Icon]) => (
          <div
            key={label}
            className="p-2.5 rounded border bg-white/3 flex items-center gap-2"
            style={{ borderColor: `${color}40` }}
          >
            <Icon size={14} style={{ color }} />
            <div className="min-w-0">
              <div className="mono text-[9px] uppercase tracking-[0.22em] text-white/45 truncate">{label}</div>
              <div className="text-base mono font-medium" style={{ color }}>{value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Warning: users over quota */}
      {totals.over_quota_users > 0 && (
        <div className="p-3 rounded-lg flex items-center gap-2 text-[12px]"
          style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.35)" }}>
          <AlertTriangle size={14} className="text-red-400" />
          <span className="text-red-200">
            {totals.over_quota_users} user{totals.over_quota_users > 1 ? "s are" : " is"} at 90%+ of their storage quota.
          </span>
        </div>
      )}

      {/* Top storage consumers strip */}
      {top_storage && top_storage.some((u) => u.storage_used_bytes > 0) && (
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.28em] text-white/45 mb-2">
            // heaviest storage users
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
            {top_storage.map((u) => (
              <div
                key={u.id}
                data-testid={`admin-top-storage-${u.username}`}
                className="p-2.5 rounded-lg bg-white/3 border border-[#a78bfa]/30"
              >
                <div className="text-[12px] text-white/90 truncate">{u.name}</div>
                <div className="mono text-[9px] text-white/45 truncate">{u.address}</div>
                <div className="mt-2 flex items-baseline justify-between">
                  <div className="mono text-[13px] text-[#6d28d9]">
                    {fmtBytes(u.storage_used_bytes)}
                  </div>
                  <div className="mono text-[10px] text-white/45">{u.files_total} file(s)</div>
                </div>
                <div className="h-1 mt-1 rounded-full bg-white/8 overflow-hidden">
                  <div
                    className="h-full"
                    style={{
                      width: `${Math.min(100, u.storage_pct)}%`,
                      background: u.storage_pct > 80 ? "#ef4444" : u.storage_pct > 50 ? "#f59e0b" : "#a78bfa",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main users table */}
      <div className="rounded-xl overflow-hidden"
        style={{ background: "#ffffff", border: "1px solid rgba(15,23,42,0.10)" }}>
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1.5fr_1fr] gap-2 px-3 py-2 border-b border-white/8 mono text-[9px] uppercase tracking-[0.22em] text-white/45">
          <div>User · Plan</div>
          <div>Signed up</div>
          <div>Last active</div>
          <div className="text-right">Files</div>
          <div>Storage</div>
          <div>Breakdown</div>
        </div>

        {sortedFiltered.length === 0 && (
          <div className="text-center py-8 text-white/40 text-xs">No users match this filter.</div>
        )}

        {sortedFiltered.map((u) => {
          const barColor = u.storage_pct > 80 ? "#ef4444" : u.storage_pct > 50 ? "#f59e0b" : "#a78bfa";
          const types = Object.entries(u.by_type || {});
          return (
            <div
              key={u.id}
              data-testid={`admin-access-row-${u.username}`}
              className="grid grid-cols-[2fr_1fr_1fr_1fr_1.5fr_1fr] gap-2 px-3 py-3 items-center border-t border-white/5 hover:bg-white/3 transition"
            >
              {/* User */}
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-[13px] text-white/90 truncate">{u.name}</div>
                  {u.is_super_admin && (
                    <span className="mono text-[8px] uppercase tracking-widest text-[#b45309] px-1.5 rounded border border-[#fbbf24]/40">super</span>
                  )}
                  {u.external_identity && (
                    <span className="mono text-[8px] uppercase tracking-widest text-[#1d4ed8] px-1.5 rounded border border-[#60a5fa]/40" title="External email identity (BYOE)">byoe</span>
                  )}
                  {u.role === "boss" && !u.is_super_admin && (
                    <span className="mono text-[8px] uppercase tracking-widest text-[#6d28d9] px-1.5 rounded border border-[#a78bfa]/40">boss</span>
                  )}
                </div>
                <div className="mono text-[10px] text-white/45 truncate">{u.address}</div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="px-1.5 py-0.5 rounded-full mono text-[9px] uppercase tracking-wider"
                    style={{ background: `${PLAN_COLORS[u.plan]}22`, color: PLAN_COLORS[u.plan], border: `1px solid ${PLAN_COLORS[u.plan]}55` }}>
                    {u.plan}
                  </span>
                  <span className="mono text-[9px] text-white/45 uppercase tracking-wider">
                    · {u.account_type}
                  </span>
                  {u.company_name && (
                    <span className="mono text-[9px] text-white/45 truncate">· {u.company_name}</span>
                  )}
                </div>
              </div>

              {/* Signed up */}
              <div className="min-w-0">
                <div className="mono text-[11px] text-white/85">{fmtDaysAgo(u.signup_days_ago)}</div>
                <div className="mono text-[9px] text-white/45 truncate">{(u.created || "").slice(0, 10)}</div>
              </div>

              {/* Last active */}
              <div className="min-w-0 flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${u.is_active ? "bg-emerald-400" : "bg-white/25"}`} />
                <div className="min-w-0">
                  <div className={`mono text-[11px] ${u.is_active ? "text-emerald-300" : "text-white/60"}`}>
                    {u.is_active ? "Active" : fmtDaysAgo(u.idle_days)}
                  </div>
                  <div className="mono text-[9px] text-white/45 truncate">{(u.last_active || "").slice(0, 10)}</div>
                </div>
              </div>

              {/* Files count */}
              <div className="text-right mono text-[13px] text-white/85">
                {u.files_total}
                <div className="mono text-[9px] text-white/40">files</div>
              </div>

              {/* Storage bar */}
              <div className="min-w-0">
                <div className="mono text-[11px]">
                  <span className="text-white/85">{fmtBytes(u.storage_used_bytes)}</span>
                  <span className="text-white/40"> / {fmtBytes(u.storage_quota_bytes)}</span>
                </div>
                <div className="h-1 mt-1 rounded-full bg-white/8 overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${Math.min(100, u.storage_pct)}%`, background: barColor }} />
                </div>
                <div className="mono text-[9px] mt-0.5" style={{ color: barColor }}>{u.storage_pct}%</div>
              </div>

              {/* Type breakdown */}
              <div className="min-w-0">
                {types.length === 0 ? (
                  <span className="mono text-[10px] text-white/35">— no files</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {types.map(([t, v]) => {
                      const c = FILE_TYPE_COLORS[t] || FILE_TYPE_COLORS.other;
                      return (
                        <span
                          key={t}
                          className="mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded flex items-center gap-1"
                          style={{
                            background: `${c}18`,
                            color: c,
                            border: `1px solid ${c}44`,
                          }}
                          title={`${v.count} ${t}(s) · ${fmtBytes(v.size)}`}
                        >
                          {t === "image" ? <ImageIcon size={8} /> : <File size={8} />}
                          {t}: {v.count}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Referrals Leaderboard Module (super-admin view of per-user coins)
// ─────────────────────────────────────────────────────────────────────
function ReferralsModule({ data, busy, onRefresh }) {
  if (busy && !data) {
    return (
      <div className="rounded-xl p-8 text-center text-white/55 text-sm"
        style={{ background: "#ffffff", border: "1px solid rgba(15,23,42,0.10)" }}>
        <Loader2 size={16} className="animate-spin mx-auto mb-2 text-[#b45309]" />
        Loading referrals leaderboard…
      </div>
    );
  }
  if (!data) return null;
  const { totals, leaderboard } = data;

  return (
    <div className="space-y-4" data-testid="admin-referrals">
      <div className="flex items-center gap-2">
        <Award size={14} className="text-[#b45309]" />
        <div className="text-sm font-medium">Referrals · Per-user coins</div>
        <span className="mono text-[10px] uppercase tracking-[0.28em] text-white/40">// leaderboard</span>
        <button
          data-testid="admin-referrals-refresh"
          onClick={onRefresh}
          disabled={busy}
          className="ml-auto btn-ghost text-xs flex items-center gap-1 px-2 py-1"
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          Refresh
        </button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          ["Users", totals.users, "#a78bfa", Users],
          ["Total referrals", totals.total_referrals, "#22c55e", UserPlus],
          ["Total coins", totals.total_coins, "#fbbf24", Award],
          ["Users w/ referrals", totals.users_with_referrals, "#60a5fa", Sparkles],
        ].map(([label, value, color, Icon]) => (
          <div key={label} className="p-2.5 rounded border bg-white/3 flex items-center gap-2" style={{ borderColor: `${color}40` }}>
            <Icon size={14} style={{ color }} />
            <div>
              <div className="mono text-[9px] uppercase tracking-[0.22em] text-white/45">{label}</div>
              <div className="text-base mono font-medium" style={{ color }}>{value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Leaderboard */}
      <div className="rounded-xl overflow-hidden"
        style={{ background: "#ffffff", border: "1px solid rgba(15,23,42,0.10)" }}>
        <div className="grid grid-cols-[40px_2fr_1.2fr_1fr_1fr_1fr] gap-2 px-3 py-2 border-b border-white/8 mono text-[9px] uppercase tracking-[0.22em] text-white/45">
          <div className="text-center">#</div>
          <div>User</div>
          <div>Code</div>
          <div className="text-right">Referrals</div>
          <div className="text-right">Coins</div>
          <div>Joined</div>
        </div>
        {leaderboard.length === 0 && (
          <div className="text-center py-8 text-white/40 text-xs">No users yet.</div>
        )}
        {leaderboard.map((u, i) => {
          const isTop = i < 3 && u.referred_count > 0;
          const medal = ["#fbbf24", "#c0c0c0", "#cd7f32"][i];
          return (
            <div
              key={u.id}
              data-testid={`admin-referrals-row-${u.username}`}
              className="grid grid-cols-[40px_2fr_1.2fr_1fr_1fr_1fr] gap-2 px-3 py-2.5 items-center border-t border-white/5 hover:bg-white/3 transition"
            >
              <div className="text-center">
                {isTop ? (
                  <div className="w-6 h-6 rounded-full grid place-items-center mono text-[10px] font-bold mx-auto"
                    style={{ background: `${medal}22`, color: medal, border: `1px solid ${medal}` }}>
                    {i + 1}
                  </div>
                ) : (
                  <span className="mono text-[11px] text-white/45">{i + 1}</span>
                )}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[12.5px] text-white/90 truncate">{u.name}</span>
                  {u.role === "boss" && <span className="mono text-[8px] uppercase tracking-widest text-[#6d28d9] px-1 rounded border border-[#a78bfa]/40">boss</span>}
                </div>
                <div className="mono text-[10px] text-white/45 truncate">{u.address}</div>
              </div>
              <div className="mono text-[10px] text-white/70">{u.referral_code || <span className="text-white/25">— none —</span>}</div>
              <div className="text-right mono text-[12px] text-[#15803d]">{u.referred_count}</div>
              <div className="text-right mono text-[13px] font-medium text-[#b45309]">{u.referral_coins}</div>
              <div className="mono text-[9.5px] text-white/45">{(u.created || "").slice(0, 10)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────
// AI SEO + GEO Module (marketing-site health for search engines & LLMs)
// ─────────────────────────────────────────────────────────────────────
function SeoGeoModule({ data, busy, onRefresh }) {
  if (busy && !data) {
    return (
      <div className="rounded-xl p-8 text-center text-white/55 text-sm"
        style={{ background: "#ffffff", border: "1px solid rgba(15,23,42,0.10)" }}>
        <Loader2 size={16} className="animate-spin mx-auto mb-2 text-[#15803d]" />
        Scanning marketing site…
      </div>
    );
  }
  if (!data) return null;

  const scoreColor = (score) => (score >= 80 ? "#22c55e" : score >= 50 ? "#fbbf24" : "#ef4444");
  const surface = data.geo.citation_surface || {};

  return (
    <div className="space-y-4" data-testid="admin-seogeo">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <Globe2 size={14} className="text-[#15803d]" />
        <div className="text-sm font-medium">AI SEO &amp; GEO Health</div>
        <span className="mono text-[10px] uppercase tracking-[0.28em] text-white/40">// {data.domain}</span>
        <button
          data-testid="admin-seogeo-refresh"
          onClick={onRefresh}
          disabled={busy}
          className="ml-auto btn-ghost text-xs flex items-center gap-1 px-2 py-1"
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          Rescan
        </button>
      </div>

      {/* Score dials */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ScoreCard title="Overall" score={data.overall_score} icon={Sparkles} color={scoreColor(data.overall_score)} />
        <ScoreCard title="SEO (search engines)" score={data.seo.score} status={data.seo.status} icon={Globe2} color={scoreColor(data.seo.score)} />
        <ScoreCard title="GEO (LLM citations)" score={data.geo.score} status={data.geo.status} icon={Sparkles} color={scoreColor(data.geo.score)} />
      </div>

      {/* Recommendations */}
      {data.recommendations && data.recommendations.length > 0 && (
        <div className="p-3 rounded-lg"
          style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.35)" }}>
          <div className="flex items-center gap-1.5 mb-1.5">
            <AlertTriangle size={12} className="text-[#b45309]" />
            <div className="mono text-[10px] uppercase tracking-[0.28em] text-[#b45309]">
              // action items ({data.recommendations.length})
            </div>
          </div>
          <ul className="text-[12px] text-white/85 space-y-1 pl-4 list-disc marker:text-[#b45309]">
            {data.recommendations.map((r) => (
              <li key={r} data-testid={`seogeo-rec-${r.slice(0,20).replace(/\s+/g,'-')}`}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {/* SEO + GEO details side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* SEO card */}
        <div className="rounded-xl overflow-hidden"
          style={{ background: "#ffffff", border: "1px solid rgba(15,23,42,0.10)" }}>
          <div className="px-4 py-2.5 border-b border-white/8 flex items-center gap-2">
            <Globe2 size={13} className="text-[#15803d]" />
            <div className="mono text-[11px] uppercase tracking-[0.22em] text-white/85">SEO checks</div>
            <div className="ml-auto text-[10px] mono text-white/45">{data.seo.checks.filter((c) => c.passed).length} / {data.seo.checks.length} passing</div>
          </div>
          <div className="p-3 space-y-1">
            {data.seo.checks.map((c) => (
              <CheckRow key={c.label} label={c.label} passed={c.passed} />
            ))}
          </div>
          <div className="px-4 py-2.5 border-t border-white/8 bg-white/3">
            <div className="mono text-[9.5px] uppercase tracking-widest text-white/40 mb-1">// assets</div>
            <div className="text-[11.5px] space-y-0.5">
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${data.seo.sitemap.exists ? "bg-emerald-400" : "bg-white/20"}`} />
                <a href={data.seo.sitemap.public_url} target="_blank" rel="noopener noreferrer"
                  className="text-white/75 hover:text-[#6d28d9] underline underline-offset-2 mono text-[11px]">
                  sitemap.xml
                </a>
                <span className="text-white/40 mono text-[10px]">({data.seo.sitemap.url_count} URLs)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${data.seo.robots.exists ? "bg-emerald-400" : "bg-white/20"}`} />
                <a href={data.seo.robots.public_url} target="_blank" rel="noopener noreferrer"
                  className="text-white/75 hover:text-[#6d28d9] underline underline-offset-2 mono text-[11px]">
                  robots.txt
                </a>
                {data.seo.robots.references_sitemap && <span className="text-emerald-300 mono text-[9px]">↔ sitemap</span>}
              </div>
            </div>
          </div>

          {/* Live meta tags */}
          <div className="px-4 py-2.5 border-t border-white/8">
            <div className="mono text-[9.5px] uppercase tracking-widest text-white/40 mb-1">// current meta</div>
            <MetaRow label="title" value={data.seo.meta.title} />
            <MetaRow label="description" value={data.seo.meta.description} />
            <MetaRow label="canonical" value={data.seo.meta.canonical} />
            <MetaRow label="og:image" value={data.seo.meta.og_image} />
          </div>
        </div>

        {/* GEO card */}
        <div className="rounded-xl overflow-hidden"
          style={{ background: "#ffffff", border: "1px solid rgba(15,23,42,0.10)" }}>
          <div className="px-4 py-2.5 border-b border-white/8 flex items-center gap-2">
            <Sparkles size={13} className="text-[#6d28d9]" />
            <div className="mono text-[11px] uppercase tracking-[0.22em] text-white/85">GEO checks</div>
            <div className="ml-auto text-[10px] mono text-white/45">{data.geo.checks.filter((c) => c.passed).length} / {data.geo.checks.length} passing</div>
          </div>
          <div className="p-3 space-y-1">
            {data.geo.checks.map((c) => (
              <CheckRow key={c.label} label={c.label} passed={c.passed} />
            ))}
          </div>
          <div className="px-4 py-2.5 border-t border-white/8 bg-white/3">
            <div className="mono text-[9.5px] uppercase tracking-widest text-white/40 mb-1">// llms.txt</div>
            <div className="flex items-center gap-2 text-[11.5px]">
              <span className={`w-1.5 h-1.5 rounded-full ${data.geo.llms_txt.exists ? "bg-emerald-400" : "bg-white/20"}`} />
              <a href={data.geo.llms_txt.public_url} target="_blank" rel="noopener noreferrer"
                className="text-white/75 hover:text-[#6d28d9] underline underline-offset-2 mono text-[11px]">
                llms.txt
              </a>
              <span className="text-white/40 mono text-[10px]">({data.geo.llms_txt.length} bytes)</span>
            </div>
            {data.geo.llms_txt.preview && (
              <pre className="mt-2 text-[10.5px] text-white/60 whitespace-pre-wrap p-2 rounded bg-black/25 max-h-24 overflow-y-auto mono">{data.geo.llms_txt.preview}...</pre>
            )}
          </div>

          {/* AI Citation surface */}
          <div className="px-4 py-2.5 border-t border-white/8">
            <div className="mono text-[9.5px] uppercase tracking-widest text-white/40 mb-2">// citation surface (which LLMs are likely to cite you)</div>
            <div className="grid grid-cols-3 gap-1.5">
              {[
                ["chatgpt", "ChatGPT"],
                ["perplexity", "Perplexity"],
                ["claude", "Claude"],
                ["gemini", "Gemini"],
                ["you_com", "You.com"],
              ].map(([key, label]) => {
                const ok = !!surface[key];
                return (
                  <div key={key}
                    className="flex items-center gap-1.5 px-2 py-1.5 rounded text-[10.5px] mono uppercase tracking-wider"
                    style={{
                      background: ok ? "rgba(34,197,94,0.08)" : "rgba(255,255,255,0.03)",
                      border: `1px solid ${ok ? "rgba(34,197,94,0.35)" : "rgba(255,255,255,0.08)"}`,
                      color: ok ? "#22c55e" : "rgba(255,255,255,0.4)",
                    }}
                    data-testid={`seogeo-surface-${key}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${ok ? "bg-emerald-400" : "bg-white/25"}`} />
                    <span className="truncate">{label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScoreCard({ title, score, status, icon: Icon, color }) {
  return (
    <div className="p-4 rounded-lg" style={{ background: `${color}0d`, border: `1px solid ${color}55` }}>
      <div className="flex items-center gap-2">
        <Icon size={14} style={{ color }} />
        <div className="mono text-[10px] uppercase tracking-[0.24em] text-white/70">{title}</div>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="text-3xl mono font-medium" style={{ color }}>{score}</div>
        <div className="mono text-[11px] text-white/45">/ 100</div>
      </div>
      {status && (
        <div className="mt-1 mono text-[9.5px] uppercase tracking-widest" style={{ color }}>
          {status.replace("_", " ")}
        </div>
      )}
      <div className="h-1 mt-2 rounded-full bg-white/8 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${score}%`, background: color }} />
      </div>
    </div>
  );
}

function CheckRow({ label, passed }) {
  return (
    <div className="flex items-center gap-2 py-0.5 text-[12px]">
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${passed ? "bg-emerald-400" : "bg-white/25"}`} />
      <span className={passed ? "text-white/85" : "text-white/50"}>{label}</span>
      {!passed && <span className="ml-auto mono text-[9px] uppercase tracking-widest text-red-400/70">missing</span>}
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="text-[11px] mono flex items-baseline gap-2 min-w-0">
      <span className="text-white/40 uppercase tracking-widest text-[9px] w-20 flex-shrink-0">{label}:</span>
      <span className={`truncate ${value ? "text-white/85" : "text-white/25"}`} title={value}>{value || "— missing —"}</span>
    </div>
  );
}


/* ==============================================================
   SEO / GEO Workspace — wraps 3 sub-tabs:
   Health (existing audit) · Site Meta · Blog Studio
   ============================================================== */
function SeoGeoWorkspace({ data, busy, onRefresh }) {
  const [sub, setSub] = useState("meta");
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 border-b border-white/10">
        {[
          { k: "meta",   label: "Site Meta",   icon: Settings2 },
          { k: "blog",   label: "Blog Studio", icon: BookOpen },
          { k: "health", label: "Health",      icon: Activity },
        ].map(t => {
          const active = sub === t.k; const Ic = t.icon;
          return (
            <button
              key={t.k}
              data-testid={`admin-seogeo-sub-${t.k}`}
              onClick={() => setSub(t.k)}
              className={`px-3 py-2 mono text-[10px] uppercase tracking-[0.28em] flex items-center gap-1.5 transition ${
                active ? "text-white border-b-2 border-[#22c55e]" : "text-white/45 hover:text-white/70"
              }`}
            >
              <Ic size={11} /> {t.label}
            </button>
          );
        })}
      </div>
      {sub === "meta"   && <SeoSiteMetaEditor />}
      {sub === "blog"   && <BlogStudio />}
      {sub === "health" && <SeoGeoModule data={data} busy={busy} onRefresh={onRefresh} />}
    </div>
  );
}


/* ==============================================================
   SEO Site Meta Editor — persisted site-wide SEO / GEO metadata
   ============================================================== */
function SeoSiteMetaEditor() {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [kwDraft, setKwDraft] = useState("");
  const [geoDraft, setGeoDraft] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/seo-settings");
      setF(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!f) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      const payload = {
        site_name: f.site_name, title: f.title, description: f.description,
        keywords: f.keywords || [], canonical: f.canonical,
        og_title: f.og_title, og_description: f.og_description, og_image: f.og_image,
        twitter_handle: f.twitter_handle, twitter_title: f.twitter_title, twitter_description: f.twitter_description,
        geo_locations: f.geo_locations || [],
        business_name: f.business_name, business_phone: f.business_phone,
        business_email: f.business_email, business_address: f.business_address, business_hours: f.business_hours,
        schema_json: f.schema_json,
      };
      const r = await api.put("/admin/seo-settings", payload);
      setF(r.data);
      setMsg("Saved · site metadata is live for the marketing site + crawlers.");
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const addKw = () => {
    const v = kwDraft.trim();
    if (!v) return;
    setF({ ...f, keywords: [...new Set([...(f.keywords || []), v])] });
    setKwDraft("");
  };
  const removeKw = (v) => setF({ ...f, keywords: (f.keywords || []).filter(k => k !== v) });
  const addGeo = () => {
    const v = geoDraft.trim();
    if (!v) return;
    setF({ ...f, geo_locations: [...new Set([...(f.geo_locations || []), v])] });
    setGeoDraft("");
  };
  const removeGeo = (v) => setF({ ...f, geo_locations: (f.geo_locations || []).filter(k => k !== v) });

  if (!f) return <div className="text-white/50 text-[12px] py-8 text-center">Loading site metadata…</div>;

  return (
    <div className="space-y-5" data-testid="admin-seo-meta-editor">
      <SeoSection title="Core metadata" icon={FileText}>
        <MetaField label="Site name"           value={f.site_name || ""}   onChange={v => setF({ ...f, site_name: v })} placeholder="HEXA OS" />
        <MetaField label="Title tag (55-60 chars ideal)"  value={f.title || ""}       onChange={v => setF({ ...f, title: v })} aiRewrite context={`Keywords: ${(f.keywords || []).join(", ")}. Site: ${f.site_name || "HEXA OS"}.`} />
        <MetaField label="Meta description (150-160 chars)" value={f.description || ""} onChange={v => setF({ ...f, description: v })} textarea aiRewrite context={`Keywords: ${(f.keywords || []).join(", ")}. Title: ${f.title || ""}.`} />
        <MetaField label="Canonical URL"       value={f.canonical || ""}   onChange={v => setF({ ...f, canonical: v })} placeholder="https://hexawebos.com" />
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-white/45 mb-1">Keywords</div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {(f.keywords || []).map(k => (
              <span key={k} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded"
                    style={{ background: "rgba(139,92,246,0.15)", color: "#c7c1ff", border: "1px solid rgba(139,92,246,0.35)" }}>
                {k} <button onClick={() => removeKw(k)} className="text-white/50 hover:text-red-300">×</button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input value={kwDraft} onChange={e => setKwDraft(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addKw(); } }}
              placeholder="Add keyword, press Enter"
              data-testid="admin-seo-keyword-input"
              className="input" style={{ flex: 1 }} />
            <button data-testid="admin-seo-keyword-add" onClick={addKw} className="btn-accent text-xs px-3 py-2">Add</button>
          </div>
        </div>
      </SeoSection>

      <SeoSection title="Open Graph / social" icon={Globe2}>
        <MetaField label="OG title"        value={f.og_title || ""}       onChange={v => setF({ ...f, og_title: v })} aiRewrite context={`Site: ${f.site_name || "HEXA OS"}. Description: ${f.description || ""}.`} />
        <MetaField label="OG description"  value={f.og_description || ""} onChange={v => setF({ ...f, og_description: v })} textarea aiRewrite context={`Title: ${f.og_title || f.title || ""}.`} />
        <MetaField label="OG image URL"    value={f.og_image || ""}       onChange={v => setF({ ...f, og_image: v })} placeholder="https://.../og.png" />
        <MetaField label="Twitter handle"       value={f.twitter_handle || ""}      onChange={v => setF({ ...f, twitter_handle: v })} placeholder="@hexawebos" />
        <MetaField label="Twitter title"        value={f.twitter_title || ""}       onChange={v => setF({ ...f, twitter_title: v })} aiRewrite context={`Site: ${f.site_name || "HEXA OS"}.`} />
        <MetaField label="Twitter description"  value={f.twitter_description || ""} onChange={v => setF({ ...f, twitter_description: v })} textarea aiRewrite context={`Twitter title: ${f.twitter_title || ""}.`} />
      </SeoSection>

      <SeoSection title="GEO / Local business" icon={MapPin}>
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-white/45 mb-1">Target locations</div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {(f.geo_locations || []).map(g => (
              <span key={g} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded"
                    style={{ background: "rgba(34,197,94,0.12)", color: "#86efac", border: "1px solid rgba(34,197,94,0.35)" }}>
                <MapPin size={9} /> {g}
                <button onClick={() => removeGeo(g)} className="text-white/50 hover:text-red-300">×</button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input value={geoDraft} onChange={e => setGeoDraft(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addGeo(); } }}
              placeholder="e.g. Bengaluru, IN"
              data-testid="admin-seo-geo-input"
              className="input" style={{ flex: 1 }} />
            <button data-testid="admin-seo-geo-add" onClick={addGeo} className="btn-accent text-xs px-3 py-2">Add</button>
          </div>
        </div>
        <MetaField label="Business name"    value={f.business_name || ""}    onChange={v => setF({ ...f, business_name: v })} />
        <MetaField label="Business phone"   value={f.business_phone || ""}   onChange={v => setF({ ...f, business_phone: v })} />
        <MetaField label="Business email"   value={f.business_email || ""}   onChange={v => setF({ ...f, business_email: v })} />
        <MetaField label="Business address" value={f.business_address || ""} onChange={v => setF({ ...f, business_address: v })} textarea />
        <MetaField label="Business hours"   value={f.business_hours || ""}   onChange={v => setF({ ...f, business_hours: v })} placeholder="Mon-Fri 09:00-18:00 IST" />
      </SeoSection>

      <SeoSection title="Advanced JSON-LD" icon={Database}>
        <MetaField label="Raw schema.org JSON-LD (paste JSON, appended to <head>)"
          value={f.schema_json || ""} onChange={v => setF({ ...f, schema_json: v })} textarea rows={6} />
      </SeoSection>

      <div className="flex flex-wrap items-center gap-2 pt-2">
        <button data-testid="admin-seo-save" onClick={save} disabled={busy}
          className="btn-accent text-xs flex items-center gap-1.5 px-4 py-2">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
          Save site metadata
        </button>
        {msg && <span className="text-emerald-300 text-xs mono">{msg}</span>}
        {err && <span className="text-red-300 text-xs mono">{err}</span>}
      </div>
    </div>
  );
}


function SeoSection({ title, icon: Icon, children }) {
  return (
    <div className="rounded-lg border border-white/10 p-4" style={{ background: "#ffffff" }}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} className="text-[#6d28d9]" />
        <div className="mono text-[10px] uppercase tracking-[0.28em] text-white/70">{title}</div>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}


function MetaField({ label, value, onChange, textarea, placeholder, rows = 2, aiRewrite = false, context = "" }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const rewrite = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/admin/seo/ai-rewrite", {
        field: (label || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").split("__")[0] || "description",
        current_value: value || "",
        context,
      });
      const suggestion = (r.data?.suggestion || "").trim();
      if (suggestion) onChange(suggestion);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
      setTimeout(() => setErr(""), 3500);
    } finally {
      setBusy(false);
    }
  };

  return (
    <label className="block">
      <div className="mono text-[10px] uppercase tracking-widest text-white/45 mb-1 flex items-center justify-between gap-2">
        <span>{label}</span>
        {aiRewrite && (
          <button
            type="button"
            onClick={rewrite}
            disabled={busy}
            data-testid={`ai-rewrite-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded flex items-center gap-1 transition"
            style={{
              background: "linear-gradient(120deg, rgba(139,92,246,0.25), rgba(56,189,248,0.25))",
              color: "#ffffff",
              border: "1px solid rgba(167,139,250,0.55)",
            }}
            title="Ask HEXA AI to rewrite this for higher CTR"
          >
            {busy ? <Loader2 size={9} className="animate-spin" /> : <Wand2 size={9} />}
            AI rewrite
          </button>
        )}
      </div>
      {textarea ? (
        <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} className="input" style={{ width: "100%" }} />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} className="input" style={{ width: "100%" }} />
      )}
      {err && <div className="text-red-300 text-[10px] mono mt-1">{err}</div>}
    </label>
  );
}


/* ==============================================================
   BLOG STUDIO — AI-powered post drafting + management
   ============================================================== */
function BlogAutomationPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [running, setRunning] = useState(false);
  const [catchingUp, setCatchingUp] = useState(false);
  const [topicInput, setTopicInput] = useState("");
  const [runMsg, setRunMsg] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/blog/automation");
      setData(r.data);
    } catch (e) { console.warn(e); }
    setBusy(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleEnabled = async () => {
    if (!data) return;
    try {
      const r = await api.put("/admin/blog/automation", { enabled: !data.enabled });
      setData(r.data);
    } catch (e) { console.warn(e); }
  };

  const addTopic = async () => {
    const t = topicInput.trim();
    if (!t || !data) return;
    try {
      const r = await api.put("/admin/blog/automation", { topics: [...(data.topics || []), t] });
      setData(r.data);
      setTopicInput("");
    } catch (e) { console.warn(e); }
  };

  const removeTopic = async (idx) => {
    if (!data) return;
    const next = (data.topics || []).filter((_, i) => i !== idx);
    try {
      const r = await api.put("/admin/blog/automation", { topics: next });
      setData(r.data);
    } catch (e) { console.warn(e); }
  };

  const runNow = async () => {
    setRunning(true); setRunMsg("");
    try {
      const r = await api.post("/admin/blog/automation/run-now");
      if (r.data?.started) {
        setRunMsg("Started in the background — refreshing automatically in ~45s…");
        setTimeout(load, 45000);
      }
    } catch (e) {
      setRunMsg(e?.response?.data?.detail || e.message);
    }
    setRunning(false);
  };

  const catchUp = async () => {
    if (!window.confirm("Publish 10 posts (catch-up) and send 10 separate email blasts to every registered user? This runs in the background and can take several minutes.")) return;
    setCatchingUp(true); setRunMsg("");
    try {
      const r = await api.post("/admin/blog/automation/catch-up");
      if (r.data?.started) {
        setRunMsg("Catch-up started in the background — refresh in a few minutes to see all 10 in Recent Runs.");
        setTimeout(load, 60000);
      }
    } catch (e) {
      setRunMsg(e?.response?.data?.detail || e.message);
    }
    setCatchingUp(false);
  };

  if (busy && !data) {
    return <div className="p-4 text-center text-white/40 text-[12px]"><Loader2 className="animate-spin inline" size={14} /></div>;
  }

  return (
    <div data-testid="admin-blog-automation" className="rounded-lg border border-white/10 p-4 space-y-3" style={{ background: "#ffffff" }}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CalendarClock size={14} className="text-[#8b5cf6]" />
          <div>
            <div className="text-[13px] font-semibold text-white/90">Auto-Publish &amp; Email Blast</div>
            <div className="text-[10.5px] text-white/45">{data?.schedule}</div>
          </div>
        </div>
        <button data-testid="admin-blog-automation-toggle" onClick={toggleEnabled}
          role="switch" aria-checked={!!data?.enabled}
          className={`w-10 h-5.5 rounded-full relative transition ${data?.enabled ? "bg-emerald-500" : "bg-slate-300"}`}
          style={{ height: 22, width: 40 }}>
          <span className="absolute top-0.5 left-0.5 rounded-full bg-white transition"
            style={{ transform: data?.enabled ? "translateX(18px)" : "translateX(0)", width: 18, height: 18 }} />
        </button>
      </div>

      <div className="flex items-center gap-4 text-[11px] text-white/60 flex-wrap">
        <span>Last run: <b className="text-white/85">{data?.last_run_date || "never"}</b></span>
        <span>Status: <b className={data?.last_run_status === "failed" ? "text-rose-400" : "text-emerald-400"}>{data?.last_run_status || "—"}</b></span>
      </div>

      <div className="space-y-1.5">
        <div className="mono text-[9px] uppercase tracking-widest text-white/40">// custom topic queue (optional — random AI topic used otherwise)</div>
        <div className="flex gap-2">
          <input data-testid="admin-blog-automation-topic-input" value={topicInput} onChange={(e) => setTopicInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addTopic()}
            placeholder="Add a topic to the queue…"
            className="flex-1 text-[12px] px-2.5 py-1.5 rounded border border-white/15 bg-white/5 text-white/85 outline-none focus:border-[#8b5cf6]" />
          <button data-testid="admin-blog-automation-topic-add" onClick={addTopic}
            className="text-[11px] px-3 py-1.5 rounded bg-[#6366f1] text-white flex items-center gap-1"><Plus size={11} /> Add</button>
        </div>
        {(data?.topics || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {data.topics.map((t, i) => (
              <span key={i} data-testid={`admin-blog-automation-topic-${i}`}
                className="text-[10.5px] px-2 py-1 rounded bg-white/8 text-white/70 flex items-center gap-1.5">
                {t}
                <button onClick={() => removeTopic(i)} className="text-white/40 hover:text-rose-300"><X size={9} /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 pt-1 flex-wrap">
        <button data-testid="admin-blog-automation-run-now" onClick={runNow} disabled={running || catchingUp}
          className="text-[11px] px-3 py-1.5 rounded flex items-center gap-1.5 text-white disabled:opacity-50"
          style={{ background: "linear-gradient(120deg,#16a34a,#22c55e)" }}>
          {running ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />} Run Now (test)
        </button>
        <button data-testid="admin-blog-automation-catchup" onClick={catchUp} disabled={running || catchingUp}
          className="text-[11px] px-3 py-1.5 rounded flex items-center gap-1.5 text-white disabled:opacity-50"
          style={{ background: "linear-gradient(120deg,#8b5cf6,#38bdf8)" }}>
          {catchingUp ? <Loader2 size={11} className="animate-spin" /> : <BookOpen size={11} />} Publish 10 Now (catch-up)
        </button>
        {runMsg && <span data-testid="admin-blog-automation-run-msg" className="text-[11px] text-white/70 flex items-center gap-1"><Mail size={11} /> {runMsg}</span>}
      </div>

      {(data?.recent_runs || []).length > 0 && (
        <div className="pt-2 border-t border-white/10">
          <div className="mono text-[9px] uppercase tracking-widest text-white/40 mb-1.5">// recent runs</div>
          <div className="space-y-1">
            {data.recent_runs.map((r) => (
              <div key={r.id} data-testid={`admin-blog-automation-run-${r.id}`} className="flex items-center gap-2 text-[10.5px] text-white/55">
                <span className={`mono uppercase px-1.5 py-0.5 rounded ${r.status === "published" ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}>{r.status}</span>
                <span className="truncate flex-1">{r.topic}</span>
                <span>{r.emailed_count || 0} emailed</span>
                <span className="text-white/30">{r.date ? new Date(r.date).toLocaleString() : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const ANALYTICS_RANGES = [
  { k: "all_time", label: "All Time" }, { k: "today", label: "Today" }, { k: "yesterday", label: "Yesterday" },
  { k: "this_week", label: "This Week" }, { k: "last_week", label: "Last Week" },
  { k: "this_month", label: "This Month" }, { k: "last_month", label: "Last Month" },
  { k: "q1", label: "Q1 (Apr–Jun)" }, { k: "q2", label: "Q2 (Jul–Sep)" }, { k: "q3", label: "Q3 (Oct–Dec)" }, { k: "q4", label: "Q4 (Jan–Mar)" },
  { k: "this_year", label: "This Year" }, { k: "last_year", label: "Last Year" },
];

function BlogAnalyticsPanel() {
  const [range, setRange] = useState("all_time");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let active = true;
    setBusy(true);
    api.get(`/admin/blog/analytics/views?range=${range}`).then((r) => { if (active) setData(r.data); }).catch(() => {}).finally(() => active && setBusy(false));
    return () => { active = false; };
  }, [range]);

  return (
    <div data-testid="admin-blog-analytics" className="rounded-lg border border-white/10 p-4 space-y-3" style={{ background: "#ffffff" }}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TrendingUp size={14} className="text-[#38bdf8]" />
          <div className="text-[13px] font-semibold text-white/90">Blog Analytics</div>
        </div>
        <select data-testid="admin-blog-analytics-range" value={range} onChange={(e) => setRange(e.target.value)}
          className="text-[11px] px-2.5 py-1.5 rounded border border-white/15 bg-white/5 text-white/85 outline-none focus:border-[#38bdf8]">
          {ANALYTICS_RANGES.map((r) => <option key={r.k} value={r.k} style={{ color: "#000" }}>{r.label}</option>)}
        </select>
      </div>

      {busy ? (
        <div className="py-6 text-center text-white/40"><Loader2 className="animate-spin inline" size={14} /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded border border-white/10 p-3">
              <div className="mono text-[9px] uppercase tracking-widest text-white/40">Readers / Views</div>
              <div className="text-2xl font-semibold text-white/90 mt-1">{data?.total_views ?? 0}</div>
            </div>
            <div className="rounded border border-white/10 p-3">
              <div className="mono text-[9px] uppercase tracking-widest text-white/40">Unique Visitors</div>
              <div className="text-2xl font-semibold text-white/90 mt-1">{data?.unique_visitors ?? 0}</div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="mono text-[9px] uppercase tracking-widest text-white/40 mb-1.5">// by country</div>
              {(data?.by_country || []).length === 0 ? (
                <div className="text-[11px] text-white/35 py-2">No views yet in this range.</div>
              ) : (
                <div className="space-y-1">
                  {data.by_country.map((c) => (
                    <div key={c.country} data-testid={`blog-analytics-country-${c.country}`} className="flex items-center justify-between text-[11px] text-white/65">
                      <span>{c.country}</span><span className="mono text-white/85">{c.views}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="mono text-[9px] uppercase tracking-widest text-white/40 mb-1.5">// by city</div>
              {(data?.by_city || []).length === 0 ? (
                <div className="text-[11px] text-white/35 py-2">No views yet in this range.</div>
              ) : (
                <div className="space-y-1">
                  {data.by_city.map((c) => (
                    <div key={c.city} data-testid={`blog-analytics-city-${c.city}`} className="flex items-center justify-between text-[11px] text-white/65">
                      <span>{c.city}</span><span className="mono text-white/85">{c.views}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function BlogStudio() {
  const [posts, setPosts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/blog/posts");
      setPosts(r.data || []);
    } catch (e) { console.warn(e); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this blog post?")) return;
    try { await api.delete(`/admin/blog/posts/${id}`); load(); } catch (e) { console.warn(e); }
  };

  const togglePublish = async (p) => {
    const next = p.status === "published" ? "draft" : "published";
    try { await api.put(`/admin/blog/posts/${p.id}`, { status: next }); load(); } catch (e) { console.warn(e); }
  };

  if (editingId || creating) {
    return (
      <BlogPostEditor
        postId={editingId}
        onClose={() => { setEditingId(null); setCreating(false); }}
        onSaved={() => { setEditingId(null); setCreating(false); load(); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-blog-studio">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[15px] font-semibold text-white">Blog Studio</div>
          <div className="text-[11px] text-white/50">
            {posts.length} post{posts.length === 1 ? "" : "s"} · Google &amp; generative crawlers get published entries via <code className="text-[#6d28d9]">/api/blog/posts</code>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="admin-blog-ai-draft" onClick={() => setAiOpen(true)}
            className="text-[11px] px-3 py-1.5 rounded-md flex items-center gap-1.5 text-white transition"
            style={{ background: "linear-gradient(120deg,#8b5cf6,#38bdf8)" }}>
            <Wand2 size={11} /> AI Draft
          </button>
          <button data-testid="admin-blog-new" onClick={() => setCreating(true)}
            className="text-[11px] px-3 py-1.5 rounded-md flex items-center gap-1 text-white"
            style={{ background: "#6366f1" }}>
            <Plus size={11} /> New Post
          </button>
        </div>
      </div>

      <BlogAutomationPanel />

      <BlogAnalyticsPanel />

      <div className="rounded-lg border border-white/10" style={{ background: "#ffffff" }}>
        {busy && posts.length === 0 && <div className="p-6 text-center text-white/40 text-[12px]">Loading…</div>}
        {!busy && posts.length === 0 && (
          <div className="p-8 text-center text-white/40 text-[12px]">
            No blog posts yet — click <b>AI Draft</b> to have Claude write one for you.
          </div>
        )}
        {posts.map(p => (
          <div key={p.id} data-testid={`admin-blog-row-${p.id}`}
               className="flex items-center gap-3 px-3 py-2.5 border-b border-white/5 hover:bg-white/3">
            <span className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ${
              p.status === "published"
                ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                : "bg-slate-500/15 text-slate-600 border border-slate-500/40"
            }`}>
              {p.status}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-white/90 text-[13px] font-medium truncate">{p.title}</div>
              <div className="text-white/40 text-[10.5px] truncate">
                /blog/{p.slug} · {(p.keywords || []).slice(0, 4).join(" · ")}
              </div>
            </div>
            <div className="text-white/40 text-[10px] mono">
              {p.views || 0} views
            </div>
            <button onClick={() => togglePublish(p)} data-testid={`admin-blog-toggle-${p.id}`}
              className={`text-[10px] px-2 py-1 rounded flex items-center gap-1 ${
                p.status === "published"
                  ? "text-slate-600 hover:bg-white/8"
                  : "text-emerald-300 hover:bg-emerald-500/10"
              }`}>
              {p.status === "published" ? <><EyeOff size={11}/> Unpublish</> : <><Eye size={11}/> Publish</>}
            </button>
            {p.status === "published" && p.slug && (
              <a
                href={`https://hexawebos.com/blog/${p.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`admin-blog-view-${p.id}`}
                className="text-[10px] px-2 py-1 text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                title="View live post"
              >
                <ExternalLink size={11}/> View
              </a>
            )}
            <button onClick={() => setEditingId(p.id)} data-testid={`admin-blog-edit-${p.id}`}
              className="text-white/60 hover:text-white/90 text-[11px] px-2 py-1">
              <Pencil size={11}/>
            </button>
            <button onClick={() => remove(p.id)} data-testid={`admin-blog-del-${p.id}`}
              className="text-rose-300/60 hover:text-rose-300 text-[11px] px-2 py-1">
              <Trash2 size={11}/>
            </button>
          </div>
        ))}
      </div>

      {aiOpen && (
        <AiBlogDraftModal
          onClose={() => setAiOpen(false)}
          onDrafted={(draft) => {
            setAiOpen(false);
            // Persist as draft immediately and open editor
            (async () => {
              try {
                const r = await api.post("/admin/blog/posts", {
                  title: draft.title, slug: draft.slug,
                  meta_description: draft.meta_description,
                  keywords: draft.keywords || [],
                  content_md: draft.content_md,
                  status: "draft",
                });
                setEditingId(r.data.id);
                load();
              } catch (e) { alert("Save failed: " + (e?.response?.data?.detail || e.message)); }
            })();
          }}
        />
      )}
    </div>
  );
}


function AiBlogDraftModal({ onClose, onDrafted }) {
  const [topic, setTopic] = useState("");
  const [kw, setKw] = useState("");
  const [tone, setTone] = useState("professional");
  const [length, setLength] = useState("medium");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const draft = async () => {
    if (!topic.trim()) { setErr("Topic required"); return; }
    setBusy(true); setErr("");
    try {
      const r = await api.post("/admin/blog/ai-draft", {
        topic: topic.trim(),
        keywords: kw.split(",").map(x => x.trim()).filter(Boolean),
        tone, length,
      });
      onDrafted(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[300] grid place-items-center p-4" style={{ background: "#f1f5f9" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-xl p-5 space-y-3"
        style={{ background: "#ffffff", border: "1px solid rgba(139,92,246,0.35)" }}
        data-testid="admin-blog-ai-modal">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-white/90">
            <Wand2 size={16} className="text-[#6d28d9]" />
            <div className="font-medium">AI Blog Draft</div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white/80"><X size={14}/></button>
        </div>
        <MetaField label="Topic" value={topic} onChange={setTopic} placeholder="e.g. How Real Estate CRM helps in Mumbai" />
        <MetaField label="Target keywords (comma-separated)" value={kw} onChange={setKw} placeholder="real estate crm, mumbai crm" />
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <div className="mono text-[10px] uppercase tracking-widest text-white/45 mb-1">Tone</div>
            <select value={tone} onChange={e => setTone(e.target.value)} className="input" style={{ width: "100%" }}>
              {["professional","casual","technical","conversational"].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="mono text-[10px] uppercase tracking-widest text-white/45 mb-1">Length</div>
            <select value={length} onChange={e => setLength(e.target.value)} className="input" style={{ width: "100%" }}>
              <option value="short">Short (~400w)</option>
              <option value="medium">Medium (~900w)</option>
              <option value="long">Long (~1500w)</option>
            </select>
          </label>
        </div>
        {err && <div className="text-red-300 text-xs mono">{err}</div>}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button onClick={onClose} disabled={busy} className="text-white/60 text-[11px] px-3 py-2">Cancel</button>
          <button data-testid="admin-blog-ai-generate" onClick={draft} disabled={busy || !topic.trim()}
            className="btn-accent text-xs px-4 py-2 flex items-center gap-1.5">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
            Draft with AI
          </button>
        </div>
      </div>
    </div>
  );
}


function BlogPostEditor({ postId, onClose, onSaved }) {
  const [f, setF] = useState({
    title: "", slug: "", meta_description: "", keywords: [], content_md: "",
    cover_image: "", schema_json: "", status: "draft",
  });
  const [kwDraft, setKwDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [preview, setPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  useEffect(() => {
    if (!postId) return;
    (async () => {
      try {
        const r = await api.get(`/admin/blog/posts/${postId}`);
        setF({ ...f, ...r.data });
      } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  const save = async (opts = {}) => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const payload = {
        title: f.title, slug: f.slug || undefined, meta_description: f.meta_description,
        keywords: f.keywords || [], content_md: f.content_md, cover_image: f.cover_image,
        schema_json: f.schema_json,
        // Only ever send status when the user explicitly asked to publish or
        // unpublish. Sending f.status meant a plain save could push the form's
        // default ("draft") over a post that was live — silently pulling a
        // published article off the public blog. The backend ignores omitted
        // fields, so leaving it out preserves whatever status the post has.
        ...(opts.publish ? { status: "published" } : opts.unpublish ? { status: "draft" } : {}),
      };
      let r;
      if (postId) r = await api.put(`/admin/blog/posts/${postId}`, payload);
      else        r = await api.post("/admin/blog/posts", payload);
      setF({ ...f, ...r.data });
      setMsg(opts.publish ? "Published!" : "Saved");
      onSaved && onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const addKw = () => { const v = kwDraft.trim(); if (!v) return; setF({ ...f, keywords: [...new Set([...(f.keywords || []), v])] }); setKwDraft(""); };
  const removeKw = (v) => setF({ ...f, keywords: (f.keywords || []).filter(k => k !== v) });

  const uploadCover = async (file) => {
    if (!file) return;
    const okType = /^image\/(jpe?g|png|gif|webp|svg\+xml)$/i.test(file.type || "");
    if (!okType) { setErr("Only JPG, PNG, GIF, WEBP or SVG images allowed"); return; }
    if (file.size > 5 * 1024 * 1024) { setErr("Image too large — max 5 MB"); return; }
    setUploading(true); setErr("");
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/admin/blog/upload-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setF({ ...f, cover_image: r.data.public_url });
      setMsg("Image uploaded — set as cover");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
    }
  };

  const onDropCover = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadCover(file);
  };

  return (
    <div className="space-y-4" data-testid="admin-blog-editor">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={onClose} className="text-white/60 hover:text-white text-[11px] flex items-center gap-1">← Back to posts</button>
        <div className="ml-auto flex items-center gap-2">
          {f.status === "published" && f.slug && (
            <a
              href={`https://hexawebos.com/blog/${f.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-blog-editor-view"
              className="text-[11px] px-3 py-1.5 rounded border border-cyan-500/30 text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
            >
              <ExternalLink size={11}/> View Live
            </a>
          )}
          <button onClick={() => setPreview(p => !p)} className="text-[11px] px-3 py-1.5 rounded border border-white/15 text-white/75 hover:bg-white/8">
            {preview ? "Edit" : "Preview"}
          </button>
          <button data-testid="admin-blog-editor-save" onClick={() => save()} disabled={busy}
            className="text-[11px] px-3 py-1.5 rounded bg-white/10 text-white/85 hover:bg-white/15 flex items-center gap-1">
            {busy ? <Loader2 size={11} className="animate-spin"/> : <Save size={11}/>} Save draft
          </button>
          <button data-testid="admin-blog-editor-publish"
            onClick={() => save({ publish: true })} disabled={busy}
            className="btn-accent text-[11px] flex items-center gap-1 px-3 py-1.5">
            <Eye size={11}/> {f.status === "published" ? "Update & keep live" : "Publish"}
          </button>
        </div>
      </div>
      {msg && <div className="text-emerald-300 text-xs mono">{msg}</div>}
      {err && <div className="text-red-300 text-xs mono">{err}</div>}

      {!preview ? (
        <div className="space-y-3">
          <MetaField label="Title (H1)" value={f.title || ""} onChange={v => setF({ ...f, title: v })} aiRewrite context={`Blog keywords: ${(f.keywords || []).join(", ")}. Meta description: ${f.meta_description || ""}.`} />
          <MetaField label="Slug (URL)" value={f.slug || ""} onChange={v => setF({ ...f, slug: v })} placeholder="auto-derived-from-title" />
          <MetaField label="Meta description" value={f.meta_description || ""} onChange={v => setF({ ...f, meta_description: v })} textarea aiRewrite context={`Blog title: ${f.title || ""}. Keywords: ${(f.keywords || []).join(", ")}.`} />

          {/* Cover image uploader — replaces the old raw-URL input. Users
              can drop a file from their desktop or click to browse. */}
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-white/45 mb-1">Cover image</div>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDropCover}
              onClick={() => fileRef.current?.click()}
              data-testid="admin-blog-cover-drop"
              className="rounded-lg border-2 border-dashed cursor-pointer transition"
              style={{
                borderColor: uploading ? "#a78bfa" : "rgba(255,255,255,0.15)",
                background: "rgba(15,23,42,0.04)",
                minHeight: 140,
              }}
            >
              {f.cover_image ? (
                <div className="flex items-center gap-3 p-3">
                  <img
                    src={f.cover_image}
                    alt="Cover preview"
                    style={{ width: 120, height: 80, objectFit: "cover", borderRadius: 6 }}
                    onError={(e) => { e.target.style.opacity = 0.3; }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-white/90 text-[12px] truncate">Cover uploaded</div>
                    <div className="text-white/45 text-[10px] mono truncate">{f.cover_image}</div>
                  </div>
                  <button
                    data-testid="admin-blog-cover-replace"
                    onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                    className="text-[10px] px-3 py-1.5 rounded border border-white/15 text-white/75 hover:bg-white/8"
                  >
                    Replace
                  </button>
                  <button
                    data-testid="admin-blog-cover-remove"
                    onClick={(e) => { e.stopPropagation(); setF({ ...f, cover_image: "" }); }}
                    className="text-[10px] px-3 py-1.5 rounded border border-rose-400/30 text-rose-300/80 hover:bg-rose-500/10"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  {uploading ? (
                    <>
                      <Loader2 size={20} className="animate-spin text-[#6d28d9] mb-2" />
                      <div className="text-white/70 text-[12px]">Uploading…</div>
                    </>
                  ) : (
                    <>
                      <ImageIcon size={22} className="text-white/45 mb-2" />
                      <div className="text-white/75 text-[12px] font-medium">Drop an image from your desktop</div>
                      <div className="text-white/40 text-[10.5px] mt-1">or click to browse · JPG / PNG / WEBP / GIF · max 5 MB</div>
                    </>
                  )}
                </div>
              )}
            </div>
            <input
              ref={fileRef}
              data-testid="admin-blog-cover-file"
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
              style={{ display: "none" }}
              onChange={(e) => uploadCover(e.target.files?.[0])}
            />
          </div>

          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-white/45 mb-1">Keywords</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(f.keywords || []).map(k => (
                <span key={k} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded"
                      style={{ background: "rgba(139,92,246,0.15)", color: "#c7c1ff", border: "1px solid rgba(139,92,246,0.35)" }}>
                  {k} <button onClick={() => removeKw(k)} className="text-white/50 hover:text-red-300">×</button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input value={kwDraft} onChange={e => setKwDraft(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addKw(); } }}
                placeholder="Add keyword" className="input" style={{ flex: 1 }} />
              <button onClick={addKw} className="btn-accent text-xs px-3 py-2">Add</button>
            </div>
          </div>
          <label className="block">
            <div className="mono text-[10px] uppercase tracking-widest text-white/45 mb-1">Content (Markdown)</div>
            <textarea rows={20} value={f.content_md || ""} onChange={e => setF({ ...f, content_md: e.target.value })}
              className="input" style={{ width: "100%", fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12 }}
              data-testid="admin-blog-editor-md" />
          </label>
          <MetaField label="Raw JSON-LD (optional, appended to article <head>)"
            value={f.schema_json || ""} onChange={v => setF({ ...f, schema_json: v })} textarea rows={4} />
        </div>
      ) : (
        <div className="p-5 rounded-lg" style={{ background: "#ffffff", border: "1px solid rgba(15,23,42,0.08)" }}>
          <div className="text-white text-[22px] font-semibold mb-2">{f.title || "Untitled"}</div>
          <div className="text-white/40 text-[11px] mb-4">/blog/{f.slug || "…"}</div>
          <article className="text-white/85 leading-relaxed" style={{ fontSize: 14 }}
            dangerouslySetInnerHTML={{ __html: (f.content_html || markdownPreview(f.content_md || "")) }} />
        </div>
      )}
    </div>
  );
}


// Tiny client-side fallback markdown renderer (backend also renders on save).
function markdownPreview(md) {
  if (!md) return "";
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const out = []; let inList = false;
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, "");
    if (/^\s*-\s+/.test(line)) {
      if (!inList) { out.push("<ul>"); inList = true; }
      out.push("<li>" + line.replace(/^\s*-\s+/, "") + "</li>");
      continue;
    }
    if (inList) { out.push("</ul>"); inList = false; }
    if (!line.trim()) { out.push(""); continue; }
    const h = line.match(/^(#{1,6})\s+(.+)$/);
    if (h) out.push(`<h${h[1].length}>${h[2]}</h${h[1].length}>`);
    else   out.push(`<p>${line}</p>`);
  }
  if (inList) out.push("</ul>");
  return out.join("\n")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" rel="noopener">$1</a>');
}


/* ─────────────────────────────  App Access Control  ───────────────────────────── */

// Lazy import of APPS to avoid a circular dep at module-load.
function useAllApps() {
  const [apps, setApps] = React.useState([]);
  React.useEffect(() => {
    let cancelled = false;
    import("./index").then((m) => { if (!cancelled) setApps(m.APPS || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return apps;
}

function GlobalAppsCard() {
  const allApps = useAllApps();
  const [disabled, setDisabled] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = React.useCallback(async () => {
    setBusy(true); setErr("");
    try { const r = await api.get("/admin/global-app-settings"); setDisabled(r.data.disabled_apps || []); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const toggle = async (appId) => {
    const next = disabled.includes(appId) ? disabled.filter((x) => x !== appId) : [...disabled, appId];
    setDisabled(next);
    try {
      await api.put("/admin/global-app-settings", { disabled_apps: next });
      setMsg(disabled.includes(appId) ? "Enabled for everyone" : "Disabled for everyone");
      setTimeout(() => setMsg(""), 1800);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
      setDisabled(disabled); // rollback
    }
  };

  return (
    <div className="rounded-xl border p-4"
      style={{ background: "#ffffff", borderColor: "#e2e8f0" }}
      data-testid="global-apps-card">
      <div className="flex items-center gap-2 mb-1">
        <Zap size={14} className="text-rose-500" />
        <div className="text-sm font-semibold text-slate-800">Global app switches</div>
        <span className="text-[10.5px] text-slate-500">// off = app disappears for every user in the workspace</span>
        <div className="ml-auto flex items-center gap-2">
          {msg && <span className="text-emerald-600 text-[10.5px] mono">{msg}</span>}
          {busy && <Loader2 size={11} className="animate-spin text-slate-400" />}
        </div>
      </div>
      {err && <div className="text-red-600 text-xs mono mb-2">{err}</div>}
      <div className="flex flex-wrap gap-1.5 mt-2">
        {allApps.map((a) => {
          const off = disabled.includes(a.id);
          return (
            <button key={a.id}
              data-testid={`global-app-${a.id}`}
              onClick={() => toggle(a.id)}
              title={off ? `Enable ${a.title} for everyone` : `Hide ${a.title} for everyone`}
              className="px-2 py-1 rounded mono text-[10px] uppercase tracking-wider transition"
              style={{
                background: off ? "rgba(239,68,68,0.12)" : "#ffffff",
                color: off ? "#ef4444" : "#334155",
                border: `1px solid ${off ? "rgba(239,68,68,0.45)" : "#e2e8f0"}`,
                textDecoration: off ? "line-through" : "none",
                opacity: off ? 0.7 : 1,
              }}>
              {a.title}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AppAccessModal({ user, onClose, onSaved }) {
  const allApps = useAllApps();
  const [enabled, setEnabled] = useState([]);
  const [initial, setInitial] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  React.useEffect(() => {
    (async () => {
      setBusy(true); setErr("");
      try {
        const r = await api.get(`/admin/users/${encodeURIComponent(user.address)}/apps`);
        const list = r.data?.enabled_apps || [];
        setEnabled(list);
        setInitial(list);
      } catch (e) { setErr(e?.response?.data?.detail || e.message); }
      finally { setBusy(false); }
    })();
  }, [user.address]);

  const restricted = enabled.length > 0;
  const toggle = (appId) => setEnabled((prev) => prev.includes(appId) ? prev.filter((x) => x !== appId) : [...prev, appId]);
  const enableAll = () => setEnabled([]);            // empty = unrestricted
  const enableEveryApp = () => setEnabled(allApps.map((a) => a.id));

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.put(`/admin/users/${encodeURIComponent(user.address)}/apps`, { enabled_apps: enabled });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[400] grid place-items-center p-4 bg-slate-900/50" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        style={{ border: "1px solid #e2e8f0", boxShadow: "0 24px 60px rgba(15,23,42,0.2)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="app-access-modal">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200">
          <div className="w-9 h-9 rounded-md grid place-items-center text-white"
            style={{ background: "linear-gradient(135deg,#38bdf8,#8b5cf6)" }}>
            <Zap size={16} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-800">App access for {user.name || user.address}</div>
            <div className="text-[11px] text-slate-500 mono">Apps NOT ticked will show as locked with a "Contact HEXA to unlock" prompt.</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-800"><X size={16} /></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="mono text-[10px] uppercase tracking-widest text-slate-500">
              {restricted ? `${enabled.length} apps enabled` : "Unrestricted — user sees every app"}
            </span>
            <div className="ml-auto flex items-center gap-1.5">
              <button onClick={enableEveryApp}
                className="text-[11px] px-3 py-1.5 rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                Enable all
              </button>
              <button onClick={enableAll}
                className="text-[11px] px-3 py-1.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50">
                Remove restriction
              </button>
              <button onClick={() => setEnabled([])}
                className="text-[11px] px-3 py-1.5 rounded border border-rose-200 text-rose-600 hover:bg-rose-50">
                Lock everything
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
            {allApps.map((a) => {
              const on = !restricted || enabled.includes(a.id);
              const Icon = a.icon;
              return (
                <button key={a.id}
                  data-testid={`app-access-${a.id}`}
                  onClick={() => toggle(a.id)}
                  className="flex items-center gap-2 px-2.5 py-2 rounded-md transition text-left"
                  style={{
                    background: on ? "rgba(139,92,246,0.08)" : "#ffffff",
                    border: `1px solid ${on ? "rgba(139,92,246,0.35)" : "#e2e8f0"}`,
                    color: on ? "#0f172a" : "#94a3b8",
                  }}>
                  <div className="grid place-items-center rounded flex-shrink-0"
                    style={{ width: 20, height: 20, background: on ? "#a78bfa" : "transparent", border: on ? "1px solid #a78bfa" : "1px solid #cbd5e1" }}>
                    {on && <Check size={12} className="text-white" strokeWidth={3} />}
                  </div>
                  {Icon && <Icon size={13} className={on ? "text-slate-700" : "text-slate-400"} />}
                  <span className="text-[12px] font-medium truncate">{a.title}</span>
                </button>
              );
            })}
          </div>

          {err && <div className="text-red-600 text-xs mono mt-3">{err}</div>}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
          <button onClick={onClose} className="text-[12px] text-slate-500 px-3 py-2">Cancel</button>
          <button onClick={save} disabled={busy || JSON.stringify(enabled.sort()) === JSON.stringify(initial.sort())}
            data-testid="app-access-save"
            className="text-[12px] px-4 py-2 rounded text-white font-medium flex items-center gap-1.5"
            style={{ background: "linear-gradient(120deg,#38bdf8,#8b5cf6)" }}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save access
          </button>
        </div>
      </div>
    </div>
  );
}




// ============================================================
// WEBSITE ANALYTICS PANEL
// ============================================================
const RANGES = [
  { k: "all",        label: "All Time" },
  { k: "today",      label: "Today" },
  { k: "yesterday",  label: "Yesterday" },
  { k: "this_week",  label: "This Week" },
  { k: "last_week",  label: "Last Week" },
  { k: "this_month", label: "This Month" },
  { k: "last_month", label: "Last Month" },
  { k: "q1",         label: "Q1 (Apr–Jun)" },
  { k: "q2",         label: "Q2 (Jul–Sep)" },
  { k: "q3",         label: "Q3 (Oct–Dec)" },
  { k: "q4",         label: "Q4 (Jan–Mar)" },
  { k: "this_year",  label: "This Year" },
  { k: "last_year",  label: "Last Year" },
];

function WebsiteAnalyticsPanel() {
  const [range, setRange] = useState("all");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (r = range) => {
    setBusy(true);
    try {
      const res = await api.get(`/admin/analytics/website?range=${r}`);
      setData(res.data);
    } catch (e) {
      console.error("Analytics load error:", e);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, [range]);

  const maxVisits = data?.countries?.length > 0 ? Math.max(...data.countries.map(c => c.visits)) : 1;

  return (
    <div className="space-y-5">
      {/* Header bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Monitor size={18} className="text-cyan-400" />
          <h2 className="text-white font-bold text-base">Website Analytics</h2>
          <span className="text-[10px] text-cyan-400/60 mono uppercase tracking-widest">hexawebos.com</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="analytics-range-select"
            value={range}
            onChange={e => setRange(e.target.value)}
            className="text-xs bg-[#0f172a] border border-white/10 text-white rounded-lg px-3 py-1.5 outline-none"
          >
            {RANGES.map(r => <option key={r.k} value={r.k}>{r.label}</option>)}
          </select>
          <button onClick={() => load()} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 transition-colors">
            <RefreshCw size={13} className={busy ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {busy && !data ? (
        <div className="flex items-center justify-center h-40"><Loader2 size={24} className="animate-spin text-cyan-400" /></div>
      ) : data ? (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Total Page Views", value: data.total_visits?.toLocaleString() || "0", color: "#06b6d4", icon: Eye },
              { label: "Unique Visitors", value: data.unique_visitors?.toLocaleString() || "0", color: "#8b5cf6", icon: Users },
              { label: "Logged-in Users", value: data.logged_in_users?.toLocaleString() || "0", color: "#22c55e", icon: Activity },
              { label: "Countries", value: data.countries?.length || "0", color: "#f59e0b", icon: Globe2 },
            ].map(({ label, value, color, icon: Icon }) => (
              <div key={label} className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
                <div className="flex items-center gap-2 mb-1">
                  <Icon size={14} style={{ color }} />
                  <span className="text-[10px] text-gray-400 uppercase tracking-widest">{label}</span>
                </div>
                <div className="text-2xl font-black" style={{ color }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Sources breakdown */}
          {data.sources?.length > 0 && (
            <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
              <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Globe2 size={14} className="text-cyan-400" /> Traffic Sources</h3>
              <div className="flex flex-wrap gap-2">
                {data.sources.map(s => (
                  <div key={s.source} className="flex items-center gap-2 px-3 py-1.5 rounded-full"
                    style={{ background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.25)" }}>
                    <span className="text-cyan-300 text-xs font-medium capitalize">{s.source}</span>
                    <span className="text-gray-400 text-xs">{s.visits.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Countries */}
            <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
              <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><MapPin size={14} className="text-purple-400" /> By Country</h3>
              {data.countries?.length === 0 ? (
                <p className="text-gray-500 text-xs text-center py-4">No data yet</p>
              ) : (
                <div className="space-y-2">
                  {data.countries.slice(0, 10).map((c, i) => (
                    <div key={c.country} className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 w-4 text-right">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs text-white truncate">{c.country}</span>
                          <span className="text-xs text-gray-400 ml-2 flex-shrink-0">{c.visits.toLocaleString()}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/5">
                          <div className="h-full rounded-full bg-gradient-to-r from-purple-500 to-cyan-500"
                            style={{ width: `${(c.visits / maxVisits) * 100}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Cities */}
            <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
              <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><MapPin size={14} className="text-cyan-400" /> By City</h3>
              {data.cities?.length === 0 ? (
                <p className="text-gray-500 text-xs text-center py-4">No data yet</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                  {data.cities.map((c, i) => (
                    <div key={`${c.country}-${c.city}`} className="flex items-center justify-between py-1 border-b border-white/[0.04]">
                      <div>
                        <span className="text-xs text-white">{c.city}</span>
                        <span className="text-[10px] text-gray-500 ml-1">({c.country})</span>
                      </div>
                      <span className="text-xs text-cyan-300 font-semibold">{c.visits.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Daily trend */}
          {data.trend?.length > 0 && (
            <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
              <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2"><TrendingUp size={14} className="text-green-400" /> Daily Trend</h3>
              <div className="flex items-end gap-1 h-24">
                {(() => {
                  const maxV = Math.max(...data.trend.map(t => t.visits), 1);
                  return data.trend.map(t => (
                    <div key={t.date} className="flex-1 flex flex-col items-center gap-1 group relative" title={`${t.date}: ${t.visits} views`}>
                      <div className="w-full rounded-sm transition-all" style={{ height: `${(t.visits / maxV) * 80}px`, background: "linear-gradient(to top, #06b6d4, #8b5cf6)", minHeight: 2 }} />
                      <span className="text-[8px] text-gray-600 rotate-45 origin-left hidden group-hover:block absolute -bottom-4 left-0 whitespace-nowrap">{t.date.slice(5)}</span>
                    </div>
                  ));
                })()}
              </div>
              <div className="flex justify-between text-[10px] text-gray-600 mt-2">
                <span>{data.trend[0]?.date}</span>
                <span>{data.trend[data.trend.length - 1]?.date}</span>
              </div>
            </div>
          )}
          {data.total_visits === 0 && (
            <div className="rounded-xl p-6 text-center" style={{ background: "rgba(6,182,212,0.05)", border: "1px dashed rgba(6,182,212,0.3)" }}>
              <Monitor size={32} className="text-cyan-400/40 mx-auto mb-2" />
              <p className="text-cyan-300/60 text-sm font-medium">No visitors tracked yet</p>
              <p className="text-gray-500 text-xs mt-1">The analytics pixel starts tracking as soon as the app receives page view events.</p>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}


// ============================================================
// CHIEF OF STAFF EMAIL PANEL
// ============================================================
function CosEmailPanel() {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [msg, setMsg] = useState("");

  const sendNow = async () => {
    setSending(true);
    setSent(false);
    try {
      const r = await api.post("/admin/cos/send-now");
      setMsg(r.data?.message || "Emails triggered!");
      setSent(r.data?.ok !== false);
    } catch (e) {
      setMsg("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSending(false);
    }
  };

  const emails = [
    { icon: "📅", title: "Work Brief", desc: "Meetings, Events, Calls for today", color: "#3b82f6" },
    { icon: "✅", title: "Task Status Report", desc: "Pending, overdue, due-today tasks", color: "#f59e0b" },
    { icon: "₹", title: "Finance Report", desc: "Cash in/out, pending invoices, paid today", color: "#22c55e" },
    { icon: "👥", title: "HR Report", desc: "Attendance, clocked-in, absent headcount", color: "#06b6d4" },
    { icon: "📊", title: "CRM Status Report", desc: "New leads, follow-ups, pipeline value, won deals", color: "#ec4899" },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <MailCheck size={18} className="text-orange-400" />
          <h2 className="text-white font-bold text-base">Chief of Staff Daily Emails</h2>
        </div>
        <button
          data-testid="cos-send-now"
          onClick={sendNow}
          disabled={sending}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all"
          style={{ background: "linear-gradient(120deg,#f97316,#ef4444)", boxShadow: "0 4px 12px rgba(249,115,22,0.3)" }}
        >
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Send Now (Test)
        </button>
      </div>

      {msg && (
        <div className={`text-sm px-4 py-3 rounded-xl flex items-center gap-2 ${sent ? "bg-green-500/10 border border-green-500/30 text-green-300" : "bg-red-500/10 border border-red-500/30 text-red-300"}`}>
          {sent ? <Check size={14} /> : <AlertTriangle size={14} />}
          {msg}
        </div>
      )}

      {/* Schedule info */}
      <div className="rounded-xl p-4" style={{ background: "rgba(249,115,22,0.05)", border: "1px solid rgba(249,115,22,0.2)" }}>
        <div className="flex items-center gap-2 mb-2">
          <Clock size={14} className="text-orange-400" />
          <span className="text-white font-semibold text-sm">Auto-Schedule: 8:00 AM IST Daily</span>
        </div>
        <p className="text-gray-400 text-xs">5 separate emails are sent automatically every morning at 8 AM IST. Use "Send Now" above to trigger immediately for testing.</p>
      </div>

      {/* Email list */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {emails.map(e => (
          <div key={e.title} className="rounded-xl p-4 flex flex-col gap-2"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
            <div className="flex items-center gap-2">
              <span className="text-xl">{e.icon}</span>
              <span className="text-white font-semibold text-sm">{e.title}</span>
            </div>
            <p className="text-gray-400 text-xs leading-relaxed">{e.desc}</p>
            <div className="mt-auto pt-1">
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: `${e.color}20`, color: e.color }}>
                Sent to: Admin + Role-based
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Role routing */}
      <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
        <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Users size={14} className="text-blue-400" /> Role-Based Routing</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { role: "CRM / Sales", gets: "CRM Status Report (assigned leads only; team leads get all reports)", color: "#ec4899" },
            { role: "Finance / Accounts", gets: "Finance Report — invoices in/out, pending, payment follow-ups", color: "#22c55e" },
            { role: "HR / Recruitment", gets: "HR Report — attendance summary and workforce overview", color: "#06b6d4" },
          ].map(r => (
            <div key={r.role} className="p-3 rounded-lg" style={{ background: `${r.color}08`, border: `1px solid ${r.color}25` }}>
              <div className="font-semibold text-xs mb-1" style={{ color: r.color }}>{r.role}</div>
              <div className="text-gray-400 text-xs leading-relaxed">{r.gets}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ============================================================
// GOOGLE ADS LEAD SYNC PANEL
// ============================================================
function GoogleAdsPanel() {
  const [config, setConfig] = useState({ webhook_key: "", owner: "", webhook_url: "", enabled: true });
  const [leads, setLeads] = useState([]);
  const [busyConfig, setBusyConfig] = useState(false);
  const [busyLeads, setBusyLeads] = useState(false);
  const [savedConfig, setSavedConfig] = useState(false);
  const [tab, setTab] = useState("leads"); // "leads" | "config"
  const [copied, setCopied] = useState(false);
  const [configError, setConfigError] = useState("");

  const loadConfig = async () => {
    try { const r = await api.get("/admin/google-ads/config"); setConfig(r.data); } catch (e) { console.error(e); }
  };
  const loadLeads = async () => {
    setBusyLeads(true);
    try { const r = await api.get("/admin/google-ads/leads?limit=100"); setLeads(r.data || []); } catch (e) { console.error(e); }
    finally { setBusyLeads(false); }
  };

  useEffect(() => { loadConfig(); loadLeads(); }, []);

  const ownerValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((config.owner || "").trim());

  const saveConfig = async () => {
    if (!config.webhook_key.trim()) { setConfigError("Set a webhook secret key first."); return; }
    if (!ownerValid) { setConfigError("Owner must be the email of the HEXA user whose CRM workspace should receive these leads."); return; }
    setConfigError("");
    setBusyConfig(true);
    try {
      await api.put("/admin/google-ads/config", { webhook_key: config.webhook_key, owner: config.owner.trim().toLowerCase(), enabled: config.enabled });
      setSavedConfig(true);
      setTimeout(() => setSavedConfig(false), 3000);
    } catch (e) { setConfigError(e?.response?.data?.detail || "Failed to save configuration."); }
    finally { setBusyConfig(false); }
  };

  const generateKey = () => {
    const key = Array.from(crypto.getRandomValues(new Uint8Array(24)), b => b.toString(16).padStart(2, "0")).join("");
    setConfig(c => ({ ...c, webhook_key: key }));
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(config.webhook_url || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const liveLeads = leads.filter(l => !l.is_test);
  const testLeads = leads.filter(l => l.is_test);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Target size={18} className="text-[#4285F4]" />
          <h2 className="text-white font-bold text-base">Google Ads Lead Sync</h2>
          <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: "rgba(66,133,244,0.15)", color: "#4285F4" }}>
            Webhook
          </span>
        </div>
        <div className="flex gap-2">
          {["leads", "config"].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`text-[11px] px-3 py-1.5 rounded-lg font-medium transition-all capitalize ${tab === t ? "bg-[#4285F4]/20 text-[#4285F4] border border-[#4285F4]/30" : "text-gray-400 hover:text-white"}`}>
              {t === "leads" ? `Leads (${liveLeads.length})` : "Configuration"}
            </button>
          ))}
        </div>
      </div>

      {tab === "leads" && (
        <>
          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Total Leads", value: liveLeads.length, color: "#4285F4" },
              { label: "Test Leads", value: testLeads.length, color: "#fbbc05" },
              { label: "Today", value: liveLeads.filter(l => l.created_at?.startsWith(new Date().toISOString().slice(0, 10))).length, color: "#34a853" },
            ].map(s => (
              <div key={s.label} className="rounded-xl p-3 text-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
                <div className="text-2xl font-black" style={{ color: s.color }}>{s.value}</div>
                <div className="text-[10px] text-gray-400 uppercase tracking-widest mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Leads table */}
          <div className="rounded-xl overflow-hidden" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)" }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
              <span className="text-white font-semibold text-sm">Ad Leads</span>
              <button onClick={loadLeads} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 transition-colors">
                <RefreshCw size={12} className={busyLeads ? "animate-spin" : ""} />
              </button>
            </div>
            {busyLeads ? (
              <div className="flex items-center justify-center h-24"><Loader2 size={20} className="animate-spin text-[#4285F4]" /></div>
            ) : leads.length === 0 ? (
              <div className="text-center py-10">
                <Target size={28} className="text-[#4285F4]/30 mx-auto mb-2" />
                <p className="text-gray-500 text-xs">No leads yet. Configure your webhook URL in the Configuration tab.</p>
              </div>
            ) : (
              <div className="divide-y divide-white/[0.05]">
                {leads.slice(0, 50).map((lead, i) => (
                  <div key={lead.lead_id} className="flex items-center gap-4 px-4 py-3 hover:bg-white/[0.02]">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ background: "rgba(66,133,244,0.15)", color: "#4285F4" }}>
                      {(lead.name || "?")[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-medium truncate">{lead.name || "Unknown"}</div>
                      <div className="text-[11px] text-gray-400 truncate">{lead.email || lead.phone || "No contact"}</div>
                    </div>
                    <div className="text-[10px] text-gray-500 hidden md:block truncate max-w-[120px]">
                      {lead.campaign_name || `Campaign ${lead.campaign_id || "?"}`}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {lead.is_test && <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: "#fbbc0520", color: "#fbbc05" }}>TEST</span>}
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: "rgba(34,197,94,0.15)", color: "#22c55e" }}>{lead.status || "new"}</span>
                    </div>
                    <div className="text-[10px] text-gray-500 hidden lg:block">{lead.created_at?.slice(0, 10) || ""}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {tab === "config" && (
        <div className="space-y-5">
          {/* How it works */}
          <div className="rounded-xl p-4" style={{ background: "rgba(66,133,244,0.05)", border: "1px solid rgba(66,133,244,0.2)" }}>
            <h3 className="text-white font-semibold text-sm mb-2 flex items-center gap-2"><Target size={14} className="text-[#4285F4]" /> How it works</h3>
            <ol className="text-gray-400 text-xs leading-relaxed space-y-1.5 list-decimal list-inside">
              <li>Generate or set a Webhook Secret Key below</li>
              <li>Copy the Webhook URL</li>
              <li>In Google Ads → Lead Form asset → Webhook integration → paste the URL and key</li>
              <li>Click "Send test data" in Google Ads to verify the connection</li>
              <li>Leads automatically appear here and sync to your CRM</li>
            </ol>
          </div>

          {/* Webhook URL */}
          <div className="rounded-xl p-4 space-y-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
            <label className="text-xs text-gray-400 uppercase tracking-widest">Webhook URL (copy this into Google Ads)</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-black/20 rounded-lg px-3 py-2 text-xs text-cyan-300 font-mono break-all">{config.webhook_url}</code>
              <button onClick={copyUrl} className="flex-shrink-0 p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 transition-colors">
                {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
              </button>
            </div>
          </div>

          {/* Webhook Key */}
          <div className="rounded-xl p-4 space-y-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
            <label className="text-xs text-gray-400 uppercase tracking-widest">Webhook Secret Key</label>
            <div className="flex items-center gap-2">
              <input
                value={config.webhook_key}
                onChange={e => setConfig(c => ({ ...c, webhook_key: e.target.value }))}
                placeholder="Enter or generate a secret key..."
                className="flex-1 bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono outline-none focus:border-[#4285F4] transition-colors"
              />
              <button onClick={generateKey} className="flex-shrink-0 px-3 py-2 rounded-lg bg-[#4285F4]/15 text-[#4285F4] text-xs font-medium hover:bg-[#4285F4]/25 transition-colors whitespace-nowrap">
                Generate
              </button>
            </div>
            <p className="text-[10px] text-gray-500">This key validates that webhook calls are genuinely from Google Ads. Keep it confidential.</p>
          </div>

          {/* Owner / CRM workspace */}
          <div className="rounded-xl p-4 space-y-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
            <label className="text-xs text-gray-400 uppercase tracking-widest">Owner (CRM workspace email)</label>
            <input
              value={config.owner}
              onChange={e => setConfig(c => ({ ...c, owner: e.target.value }))}
              placeholder="e.g. sales@yourcompany.com"
              className={`w-full bg-black/20 border rounded-lg px-3 py-2 text-sm text-white font-mono outline-none transition-colors ${
                config.owner && !ownerValid ? "border-red-500/60 focus:border-red-500" : "border-white/10 focus:border-[#4285F4]"
              }`}
            />
            <p className="text-[10px] text-gray-500">The webhook key resolves to this HEXA user's CRM workspace — every lead is saved there, never to the super-admin account.</p>
            {config.owner && !ownerValid && <p className="text-[10px] text-red-400">Enter a valid email address.</p>}
          </div>

          {configError && <p className="text-[11px] text-red-400">{configError}</p>}

          <button onClick={saveConfig} disabled={busyConfig}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-60"
            style={{ background: "linear-gradient(120deg,#4285F4,#34a853)", boxShadow: "0 4px 12px rgba(66,133,244,0.2)" }}>
            {busyConfig ? <Loader2 size={14} className="animate-spin" /> : savedConfig ? <Check size={14} /> : <Save size={14} />}
            {savedConfig ? "Saved!" : "Save Configuration"}
          </button>

          {/* CRM sync info */}
          <div className="rounded-xl p-4" style={{ background: "rgba(34,197,94,0.05)", border: "1px solid rgba(34,197,94,0.2)" }}>
            <div className="flex items-center gap-2 mb-1">
              <Activity size={13} className="text-green-400" />
              <span className="text-white text-xs font-semibold">Auto-sync to CRM</span>
            </div>
            <p className="text-gray-400 text-xs">Every real Google Ads lead (not test) is automatically added to the Owner's CRM workspace above, with source "google_ads". Open the CRM app (as that user) to view and manage them.</p>
          </div>
        </div>
      )}
    </div>
  );
}



// ============ PENDING APPROVALS PANEL (Part B) ============
function PendingApprovalsPanel() {
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [status, setStatus] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users/pending-approvals");
      setUsers(data || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    setActionId(id); setBusy(true); setStatus("");
    try {
      await api.post(`/admin/users/${id}/approve`);
      setStatus("Account approved — confirmation email sent.");
      await load();
    } catch (e) {
      setStatus("Error: " + (e?.response?.data?.detail || "unknown"));
    }
    setBusy(false); setActionId(null);
  };

  const reject = async (id) => {
    if (!window.confirm("Reject and permanently delete this account?")) return;
    setActionId(id); setBusy(true); setStatus("");
    try {
      await api.post(`/admin/users/${id}/reject`);
      setStatus("Account rejected and removed.");
      await load();
    } catch (e) {
      setStatus("Error: " + (e?.response?.data?.detail || "unknown"));
    }
    setBusy(false); setActionId(null);
  };

  return (
    <div className="space-y-4 mt-4" data-testid="pending-approvals-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-gray-900">Pending Business Approvals</h3>
          <p className="text-xs text-gray-500 mt-0.5">Business accounts must be approved before they can sign in.</p>
        </div>
        <button onClick={load} className="p-2 text-gray-400 hover:text-gray-700" title="Refresh">
          <RefreshCw size={14} />
        </button>
      </div>
      {status && (
        <div className={`text-xs px-3 py-2 rounded font-medium ${status.startsWith("Error") ? "bg-red-50 text-red-600 border border-red-100" : "bg-green-50 text-green-700 border border-green-100"}`}>
          {status}
        </div>
      )}
      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 text-sm"><Loader2 size={14} className="animate-spin" /> Loading pending registrations…</div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <CheckCircle2 size={32} className="mx-auto mb-2 text-green-400" />
          <div className="text-sm font-medium">No pending approvals</div>
          <div className="text-xs mt-1">All business accounts are up to date.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {users.map(u => (
            <div
              key={u.id}
              data-testid={`approval-row-${u.id}`}
              className="flex items-center gap-4 p-4 rounded-xl border border-amber-100 bg-amber-50/50"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {(u.name || u.username || "?")[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-gray-900 truncate">{u.name || u.username}</div>
                <div className="text-xs text-gray-500 truncate">{u.address}</div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {u.company_name && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded font-medium">{u.company_name}</span>}
                  <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded font-medium uppercase">{u.plan || "business"}</span>
                  {u.phone && <span className="text-xs text-gray-400">{u.phone}</span>}
                  <span className="text-xs text-gray-400">{u.created ? new Date(u.created).toLocaleDateString() : ""}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  data-testid={`approve-btn-${u.id}`}
                  onClick={() => approve(u.id)}
                  disabled={busy && actionId === u.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg transition-all"
                  style={{ background: "#22c55e" }}
                >
                  {busy && actionId === u.id ? <Loader2 size={11} className="animate-spin" /> : <UserCheck size={11} />}
                  Approve
                </button>
                <button
                  data-testid={`reject-btn-${u.id}`}
                  onClick={() => reject(u.id)}
                  disabled={busy && actionId === u.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg transition-all"
                  style={{ background: "#ef4444" }}
                >
                  {busy && actionId === u.id ? <Loader2 size={11} className="animate-spin" /> : <UserX size={11} />}
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
